import React, { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import API_BASE_URL from '../config/api';
import SEO from '../components/SEO';
import NewTradeDialog from '../components/NewTradeDialog';
import { getTradeRowColor, formatTradeStatusCompact } from '../utils/tradeStatusUtils';
import '../styles/Trades.css';

const Trades = () => {
  // Configurable decimal places for display
  const DECIMAL_PLACES = 4;

  const [searchParams, setSearchParams] = useSearchParams();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [prefillData, setPrefillData] = useState(null);
  const [trades, setTrades] = useState([]);
  const [fundings, setFundings] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());

  const navigate = useNavigate();

  // Pagination state — persisted in sessionStorage so navigation away and back restores position
  const [currentPage, setCurrentPage] = useState(() => {
    const saved = sessionStorage.getItem('trades_currentPage');
    return saved ? parseInt(saved, 10) : 1;
  });
  const [pageSize, setPageSize] = useState(() => {
    const saved = sessionStorage.getItem('trades_pageSize');
    if (!saved) return 25;
    return saved === 'unlimited' ? 'unlimited' : parseInt(saved, 10);
  });
  const [totalRecords, setTotalRecords] = useState(0);

  // Multi-select state for bulk actions
  const [selectedTradeIds, setSelectedTradeIds] = useState([]); // Array of selected trade IDs
  const [bulkAction, setBulkAction] = useState(''); // Current bulk action selected

  // Bulk trade job dialog state
  const [showBulkTradeJobDialog, setShowBulkTradeJobDialog] = useState(false);
  const [bulkTradeJobDialogData, setBulkTradeJobDialogData] = useState({
    actionType: 'OPEN-TRADE',
    triggerType: 'TRADE_PROFIT',
    condition: '>',
    tradeProfit: '',
    volume: '',
    scheduledTime: ''
  });

  // Mobile view state
  const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 768); // Track if mobile view
  const [pressingTradeId, setPressingTradeId] = useState(null); // Track which trade is being pressed (mousedown/touchstart)

  // Helper function to check if value should be greyed out
  const getValueClass = (value) => {
    const strValue = String(value);
    return (strValue === 'N/A' || strValue === '---') ? 'detail-value na-value' : 'detail-value';
  };


  // Helper function to format numbers for display with configurable decimal places
  const formatForDisplay = (value, decimalPlaces = DECIMAL_PLACES) => {
    // If value is N/A or ---, return as is
    if (value === 'N/A' || value === '---' || value === null || value === undefined) {
      return value;
    }

    // Convert to number
    const numValue = typeof value === 'string' ? parseFloat(value) : value;

    // If not a valid number, return original value
    if (isNaN(numValue)) {
      return value;
    }

    // Round to specified decimal places
    return numValue.toFixed(decimalPlaces);
  };

  // Helper function to format numbers with thousand separators (spaces)
  const formatNumberAsString = (value) => {
    // If value is N/A or ---, return as string
    if (value === 'N/A' || value === '---') {
      return String(value);
    }

    // If null or undefined, return as string
    if (value === null || value === undefined) {
      return String(value);
    }

    // Convert to string and parse as float
    const numValue = parseFloat(value);

    // If not a valid number, return original value as string
    if (isNaN(numValue)) {
      return String(value);
    }

    // If number is less than 1000, return as string without formatting
    if (Math.abs(numValue) < 1000) {
      return String(value);
    }

    // Split into integer and decimal parts
    const parts = String(value).split('.');
    const integerPart = parts[0];
    const decimalPart = parts[1] ? '.' + parts[1] : '';

    // Add space separators every 3 digits from the right
    const formatted = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

    return formatted + decimalPart;
  };

  // Calculate next funding time (funding happens every 8 hours: 00:00, 08:00, 16:00 UTC)
  const getNextFundingTime = () => {
    const now = new Date();
    const nextFunding = new Date(now);

    // Get current hour in UTC
    const currentHourUTC = now.getUTCHours();

    // Funding times in UTC: 00:00, 08:00, 16:00
    let nextFundingHour;
    if (currentHourUTC < 8) {
      nextFundingHour = 8;
    } else if (currentHourUTC < 16) {
      nextFundingHour = 16;
    } else {
      // After 16:00, next funding is tomorrow at 00:00
      nextFundingHour = 0;
      nextFunding.setUTCDate(nextFunding.getUTCDate() + 1);
    }

    nextFunding.setUTCHours(nextFundingHour, 0, 0, 0);
    return nextFunding;
  };

  // Format next funding time for display as YYYY-MM-DD hh:mm
  const formatNextFundingTime = (date) => {
    if (!date) return '-';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };

  // Format timestamp for display as YYYY-MM-DD HH:mm:ss
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '---';
    const date = new Date(timestamp);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  // Format date for created timestamp column (two lines: date and time)
  const formatCreatedTimestamp = (dateString) => {
    if (!dateString) return { date: '-', time: '-' };

    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return { date: '-', time: '-' };
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return {
      date: `${year}-${month}-${day}`,
      time: `${hours}:${minutes}`
    };
  };


  // Format countdown as hh:mm:ss and return color based on time remaining
  const formatCountdown = (futureDate) => {
    if (!futureDate) return { text: '--:--:--', color: '#8b949e' };

    const now = currentTime;
    const diff = futureDate - now;

    if (diff <= 0) return { text: '00:00:00', color: '#8b949e' };

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    const totalMinutes = hours * 60 + minutes;

    // Determine color based on time remaining
    let color;
    if (totalMinutes >= 60) {
      color = '#3fb950'; // Green (more than 1 hour)
    } else if (totalMinutes >= 30) {
      color = '#ff9800'; // Orange (30 min to 1 hour)
    } else {
      color = '#ff6b6b'; // Light red (less than 30 min)
    }

    return {
      text: `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`,
      color: color
    };
  };

  const [tradeData, setTradeData] = useState({
    tradeId: '---',
    tradeStatus: '---',
    realizedProfitPercent: '---',
    realizedProfitUsdt: '---',
    exchange1: {
      name: '---',
      symbol: '---',
      contract: '---',
      positionId: '---',
      fundingFee: '---',
      takerFee: '---',
      makerFee: '---',
      orderBook: {
        bestSell: '---',
        actualPrice: '---',
        bestBuy: '---',
        takerPrice: '---',
        makerPrice: '---',
        depthToMove: '---',
        depthToMoveVolume: '---'
      },
      position: {
        status: 'pending', // 'pending', 'open', 'closed'
        opened: false,
        openedAt: '---',
        openingTimestamp: '---',
        actualPrice: '---',
        openingFee: '---',
        openingFeePercent: '---',
        openingPaidFeeUsdt: '---',
        paidForPosition: '---',
        closedAt: '---',
        closingFee: '---',
        closingFeePercent: '---',
        closingPaidFeeUsdt: '---',
        closedPositionFor: '---',
        actualRealizedFundings: '---'
      }
    },
    exchange2: {
      name: '---',
      symbol: '---',
      contract: '---',
      positionId: '---',
      fundingFee: '---',
      takerFee: '---',
      makerFee: '---',
      orderBook: {
        bestSell: '---',
        actualPrice: '---',
        bestBuy: '---',
        takerPrice: '---',
        makerPrice: '---',
        depthToMove: '---',
        depthToMoveVolume: '---'
      },
      position: {
        status: 'pending', // 'pending', 'open', 'closed'
        opened: false,
        openedAt: '---',
        openingTimestamp: '---',
        actualPrice: '---',
        openingFee: '---',
        openingFeePercent: '---',
        openingPaidFeeUsdt: '---',
        paidForPosition: '---',
        closedAt: '---',
        closingFee: '---',
        closingFeePercent: '---',
        closingPaidFeeUsdt: '---',
        closedPositionFor: '---',
        actualRealizedFundings: '---'
      }
    },
    hedgeBalancedProfit: '---',
    hedgeBalancedProfitUsdt: '---',
    hedgeBalancedProfitPercent: '---',
    finalProfits: {
      ex1Taker_ex2Taker: '---',
      ex1Taker_ex2Maker: '---',
      ex1Maker_ex2Taker: '---',
      ex1Maker_ex2Maker: '---'
    }
  });

  // Store exchange IDs and symbolId for periodic orderbook fetching
  const [exchange1Id, setExchange1Id] = useState(null);
  const [exchange2Id, setExchange2Id] = useState(null);
  const [symbolId, setSymbolId] = useState(null);
  const [exchange1Contract, setExchange1Contract] = useState(null);
  const [exchange2Contract, setExchange2Contract] = useState(null);

  // Volume input states for each exchange
  const [volume1, setVolume1] = useState('');
  const [volume2, setVolume2] = useState('');

  useEffect(() => {
    // Check for URL parameters to prefill trade dialog on mount
    const symbol = searchParams.get('symbol');
    const ex1 = searchParams.get('ex1');
    const ex2 = searchParams.get('ex2');
    const ex1Contract = searchParams.get('ex1Contract');
    const ex2Contract = searchParams.get('ex2Contract');

    if (symbol && ex1 && ex2) {
      setPrefillData({
        symbol,
        ex1,
        ex2,
        ex1Contract: ex1Contract || 'SPOT',
        ex2Contract: ex2Contract || 'SPOT'
      });
      setIsDialogOpen(true);
      // Clear URL parameters after reading them
      setSearchParams({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    sessionStorage.setItem('trades_currentPage', currentPage);
    sessionStorage.setItem('trades_pageSize', pageSize);
    fetchTrades();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize]);

  // Update current time every second for countdown
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Periodic orderbook fetching effect
  useEffect(() => {
    if (!exchange1Id || !exchange2Id || !symbolId || !exchange1Contract || !exchange2Contract) {
      return; // Don't fetch if we don't have the necessary data
    }

    // Function to fetch orderbook for both exchanges
    const fetchOrderbooks = async () => {
      try {
        // Determine market type for Exchange 1 based on contract
        const market1 = exchange1Contract.toUpperCase() === 'SPOT' ? 'spot' : 'perpetual';

        // Fetch orderbook for Exchange 1
        const response1 = await fetch(
          `${API_BASE_URL}/api/orderbook?exchangeId=${exchange1Id}&symbolId=${symbolId}&limit=5&market=${market1}`
        );
        const data1 = await response1.json();

        if (data1.success) {
          setTradeData(prev => {
            const bestBid = data1.data.bestBid.price;
            const actualPrice = data1.data.actualPrice;
            const bestAsk = data1.data.bestAsk.price;

            // Parse taker and maker fees (they are stored as percentages like "0.0500")
            const takerFeePercent = parseFloat(prev.exchange1.takerFee) || 0;
            const makerFeePercent = parseFloat(prev.exchange1.makerFee) || 0;

            // Convert percentage to decimal (e.g., 0.05% -> 0.0005)
            const takerFeeDecimal = takerFeePercent / 100;
            const makerFeeDecimal = makerFeePercent / 100;

            // Calculate Taker price = Best ask * (1 + Taker fee)
            const takerPrice = bestAsk * (1 + takerFeeDecimal);

            // Calculate Maker price = Actual price * (1 + Maker fee)
            const makerPrice = actualPrice * (1 + makerFeeDecimal);

            // Calculate PNL for open and closed positions (store raw values, no rounding)
            let actualPnl = '---';
            let actualPnlUsdt = '---';

            if (prev.exchange1.position.status === 'closed') {
              // For closed positions: PNL = "closed position for" - "paid for position"
              if (prev.exchange1.position.closedPositionFor !== '---' && prev.exchange1.position.paidForPosition !== '---') {
                const closedPositionFor = parseFloat(prev.exchange1.position.closedPositionFor.replace(/\s/g, ''));
                const paidForPosition = parseFloat(prev.exchange1.position.paidForPosition.replace(/\s/g, ''));
                actualPnlUsdt = closedPositionFor - paidForPosition;
                actualPnl = (closedPositionFor - paidForPosition) / paidForPosition * 100;
                if (prev.exchange1.contract.toUpperCase() === 'SHORT') {
                                actualPnlUsdt = actualPnlUsdt * (-1);
                                actualPnl = actualPnl * (-1);
                }
              }
            } else if (prev.exchange1.position.status === 'open' && prev.exchange1.position.openingPriceRaw) {
              // For open positions: use current actual price
              const openingPrice = parseFloat(prev.exchange1.position.openingPriceRaw);
              const openingVolume = parseFloat(prev.exchange1.position.openingVolumeRaw || 1);
              let priceDiff = actualPrice - openingPrice;
              if (prev.exchange1.contract.toUpperCase() === 'SHORT') {
                priceDiff = priceDiff * (-1) * openingVolume;
              } else {
                priceDiff = priceDiff * openingVolume;
              }
              actualPnlUsdt = priceDiff;

              let pnlPercent = (priceDiff / openingPrice) * 100;
              actualPnl = pnlPercent;
            }

            // Calculate hedge balanced profit (sum of both exchanges + fundings)
            let hedgeBalancedProfitUsdt = '---';
            let hedgeBalancedProfitPercent = '---';

            if (actualPnlUsdt !== '---' && prev.exchange2.position.actualPnlUsdt !== '---') {
              const ex1UsdtValue = typeof actualPnlUsdt === 'number' ? actualPnlUsdt : parseFloat(actualPnlUsdt);
              const ex2UsdtValue = typeof prev.exchange2.position.actualPnlUsdt === 'number' ? prev.exchange2.position.actualPnlUsdt : parseFloat(prev.exchange2.position.actualPnlUsdt);

              // Add fundings if available
              const ex1FundingValue = prev.exchange1.position.actualRealizedFundings !== '---' && prev.exchange1.position.actualRealizedFundings !== 'N/A' ? parseFloat(prev.exchange1.position.actualRealizedFundings) : 0;
              const ex2FundingValue = prev.exchange2.position.actualRealizedFundings !== '---' && prev.exchange2.position.actualRealizedFundings !== 'N/A' ? parseFloat(prev.exchange2.position.actualRealizedFundings) : 0;

              hedgeBalancedProfitUsdt = ex1UsdtValue + ex2UsdtValue + ex1FundingValue + ex2FundingValue;

              const ex1PercentValue = typeof actualPnl === 'number' ? actualPnl : parseFloat(actualPnl);
              const ex2PercentValue = typeof prev.exchange2.position.actualPnl === 'number' ? prev.exchange2.position.actualPnl : parseFloat(prev.exchange2.position.actualPnl);
              hedgeBalancedProfitPercent = ex1PercentValue + ex2PercentValue;
            }

            return {
              ...prev,
              exchange1: {
                ...prev.exchange1,
                orderBook: {
                  ...prev.exchange1.orderBook,
                  bestSell: bestBid,
                  actualPrice: actualPrice,
                  bestBuy: bestAsk,
                  takerPrice: takerPrice,
                  makerPrice: makerPrice
                },
                position: {
                  ...prev.exchange1.position,
                  actualPnl: actualPnl,
                  actualPnlUsdt: actualPnlUsdt
                }
              },
              hedgeBalancedProfit: hedgeBalancedProfitUsdt !== '---' ?
                `${formatForDisplay(hedgeBalancedProfitUsdt)} USDT (${formatForDisplay(hedgeBalancedProfitPercent)} %)` : '---',
              hedgeBalancedProfitUsdt: hedgeBalancedProfitUsdt,
              hedgeBalancedProfitPercent: hedgeBalancedProfitPercent
            };
          });
        }

        // Determine market type for Exchange 2 based on contract
        const market2 = exchange2Contract.toUpperCase() === 'SPOT' ? 'spot' : 'perpetual';

        // Fetch orderbook for Exchange 2
        const response2 = await fetch(
          `${API_BASE_URL}/api/orderbook?exchangeId=${exchange2Id}&symbolId=${symbolId}&limit=5&market=${market2}`
        );
        const data2 = await response2.json();

        if (data2.success) {
          setTradeData(prev => {
            const bestBid = data2.data.bestBid.price;
            const actualPrice = data2.data.actualPrice;
            const bestAsk = data2.data.bestAsk.price;

            // Parse taker and maker fees (they are stored as percentages like "0.0500")
            const takerFeePercent = parseFloat(prev.exchange2.takerFee) || 0;
            const makerFeePercent = parseFloat(prev.exchange2.makerFee) || 0;

            // Convert percentage to decimal (e.g., 0.05% -> 0.0005)
            const takerFeeDecimal = takerFeePercent / 100;
            const makerFeeDecimal = makerFeePercent / 100;

            // Calculate Taker price = Best ask * (1 + Taker fee)
            const takerPrice = bestAsk * (1 + takerFeeDecimal);

            // Calculate Maker price = Actual price * (1 + Maker fee)
            const makerPrice = actualPrice * (1 + makerFeeDecimal);

            // Calculate PNL for open and closed positions (store raw values, no rounding)
            let actualPnl = '---';
            let actualPnlUsdt = '---';

            if (prev.exchange2.position.status === 'closed') {
              // For closed positions: PNL = "closed position for" - "paid for position"
              if (prev.exchange2.position.closedPositionFor !== '---' && prev.exchange2.position.paidForPosition !== '---') {
                const closedPositionFor = parseFloat(prev.exchange2.position.closedPositionFor.replace(/\s/g, ''));
                const paidForPosition = parseFloat(prev.exchange2.position.paidForPosition.replace(/\s/g, ''));
                actualPnlUsdt = closedPositionFor - paidForPosition;
                actualPnl = (closedPositionFor - paidForPosition) / paidForPosition * 100;
                if (prev.exchange2.contract.toUpperCase() === 'SHORT') {
                                actualPnlUsdt = actualPnlUsdt * (-1);
                                actualPnl = actualPnl * (-1);
                }
              }
            } else if (prev.exchange2.position.status === 'open' && prev.exchange2.position.openingPriceRaw) {
              // For open positions: use current actual price
              const openingPrice = parseFloat(prev.exchange2.position.openingPriceRaw);
              const openingVolume = parseFloat(prev.exchange2.position.openingVolumeRaw || 1);
              let priceDiff = actualPrice - openingPrice;
              if (prev.exchange2.contract.toUpperCase() === 'SHORT') {
                priceDiff = priceDiff * (-1) * openingVolume;
              } else {
                priceDiff = priceDiff * openingVolume;
              }
              actualPnlUsdt = priceDiff;

              let pnlPercent = (priceDiff / openingPrice) * 100;
              actualPnl = pnlPercent;
            }

            // Calculate hedge balanced profit (sum of both exchanges + fundings)
            let hedgeBalancedProfitUsdt = '---';
            let hedgeBalancedProfitPercent = '---';

            if (actualPnlUsdt !== '---' && prev.exchange1.position.actualPnlUsdt !== '---') {
              const ex1UsdtValue = typeof prev.exchange1.position.actualPnlUsdt === 'number' ? prev.exchange1.position.actualPnlUsdt : parseFloat(prev.exchange1.position.actualPnlUsdt);
              const ex2UsdtValue = typeof actualPnlUsdt === 'number' ? actualPnlUsdt : parseFloat(actualPnlUsdt);

              // Add fundings if available
              const ex1FundingValue = prev.exchange1.position.actualRealizedFundings !== '---' && prev.exchange1.position.actualRealizedFundings !== 'N/A' ? parseFloat(prev.exchange1.position.actualRealizedFundings) : 0;
              const ex2FundingValue = prev.exchange2.position.actualRealizedFundings !== '---' && prev.exchange2.position.actualRealizedFundings !== 'N/A' ? parseFloat(prev.exchange2.position.actualRealizedFundings) : 0;

              hedgeBalancedProfitUsdt = ex1UsdtValue + ex2UsdtValue + ex1FundingValue + ex2FundingValue;

              const ex1PercentValue = typeof prev.exchange1.position.actualPnl === 'number' ? prev.exchange1.position.actualPnl : parseFloat(prev.exchange1.position.actualPnl);
              const ex2PercentValue = typeof actualPnl === 'number' ? actualPnl : parseFloat(actualPnl);
              hedgeBalancedProfitPercent = ex1PercentValue + ex2PercentValue;
            }

            return {
              ...prev,
              exchange2: {
                ...prev.exchange2,
                orderBook: {
                  ...prev.exchange2.orderBook,
                  bestSell: bestBid,
                  actualPrice: actualPrice,
                  bestBuy: bestAsk,
                  takerPrice: takerPrice,
                  makerPrice: makerPrice
                },
                position: {
                  ...prev.exchange2.position,
                  actualPnl: actualPnl,
                  actualPnlUsdt: actualPnlUsdt
                }
              },
              hedgeBalancedProfit: hedgeBalancedProfitUsdt !== '---' ?
                `${formatForDisplay(hedgeBalancedProfitUsdt)} USDT (${formatForDisplay(hedgeBalancedProfitPercent)} %)` : '---',
              hedgeBalancedProfitUsdt: hedgeBalancedProfitUsdt,
              hedgeBalancedProfitPercent: hedgeBalancedProfitPercent
            };
          });
        }

        // Fetch Depth To Move for Exchange 1
        try {
          const dtmResponse1 = await fetch(
            `${API_BASE_URL}/api/orderbook/depth-to-move?exchangeId=${exchange1Id}&symbolId=${symbolId}&market=${market1}`
          );
          const dtmData1 = await dtmResponse1.json();

          if (dtmData1.success) {
            setTradeData(prev => ({
              ...prev,
              exchange1: {
                ...prev.exchange1,
                orderBook: {
                  ...prev.exchange1.orderBook,
                  depthToMove: dtmData1.data.depth.price.toFixed(2),
                  depthToMoveVolume: dtmData1.data.depth.quantity.toFixed(2)
                }
              }
            }));
          }
        } catch (error) {
          console.error('Error fetching DTM for Exchange 1:', error);
        }

        // Fetch Depth To Move for Exchange 2
        try {
          const dtmResponse2 = await fetch(
            `${API_BASE_URL}/api/orderbook/depth-to-move?exchangeId=${exchange2Id}&symbolId=${symbolId}&market=${market2}`
          );
          const dtmData2 = await dtmResponse2.json();

          if (dtmData2.success) {
            setTradeData(prev => ({
              ...prev,
              exchange2: {
                ...prev.exchange2,
                orderBook: {
                  ...prev.exchange2.orderBook,
                  depthToMove: dtmData2.data.depth.price.toFixed(2),
                  depthToMoveVolume: dtmData2.data.depth.quantity.toFixed(2)
                }
              }
            }));
          }
        } catch (error) {
          console.error('Error fetching DTM for Exchange 2:', error);
        }
      } catch (error) {
        console.error('Error fetching orderbooks:', error);
      }
    };

    // Fetch immediately
    fetchOrderbooks();

    // Set up periodic fetching every 3 seconds
    const intervalId = setInterval(fetchOrderbooks, 3000);

    // Cleanup interval on unmount or when dependencies change
    return () => clearInterval(intervalId);
  }, [exchange1Id, exchange2Id, symbolId, exchange1Contract, exchange2Contract]);

  const fetchTrades = async () => {
    try {
      const params = new URLSearchParams({
        page: currentPage,
        pageSize: pageSize === 'unlimited' ? 'unlimited' : pageSize
      });
      const response = await fetch(`${API_BASE_URL}/api/trades?${params}`);
      const data = await response.json();
      if (data.success) {
        setTrades(data.data);
        setTotalRecords(data.totalRecords || 0);
      }
    } catch (error) {
      console.error('Error fetching trades:', error);
    }
  };

  const handleTradeCreated = () => {
    fetchTrades();
  };

  const fetchFundings = async (tradeId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/trades/${tradeId}/fundings`);
      const data = await response.json();

      if (data.success) {
        setFundings(data.data);
        calculateRealizedFundings(data.data);
      }
    } catch (error) {
      console.error('Error fetching fundings:', error);
      setFundings([]);
    }
  };

  // Calculate the sum of realized fundings for each position
  const calculateRealizedFundings = (fundingsData) => {
    let ex1Total = 0;
    let ex2Total = 0;

    fundingsData.forEach(funding => {
      if (funding.ex1_funding !== null) {
        ex1Total += parseFloat(funding.ex1_funding);
      }
      if (funding.ex2_funding !== null) {
        ex2Total += parseFloat(funding.ex2_funding);
      }
    });

    // Update tradeData with calculated fundings
    setTradeData(prev => {
      const ex1Contract = prev.exchange1.contract.toUpperCase();
      const ex2Contract = prev.exchange2.contract.toUpperCase();

      // Only show fundings for LONG or SHORT contracts (store raw values)
      const ex1Fundings = (ex1Contract === 'LONG' || ex1Contract === 'SHORT')
        ? ex1Total
        : 'N/A';
      const ex2Fundings = (ex2Contract === 'LONG' || ex2Contract === 'SHORT')
        ? ex2Total
        : 'N/A';

      // Recalculate hedge balanced profit including fundings
      let hedgeBalancedProfitUsdt = prev.hedgeBalancedProfitUsdt;
      let hedgeBalancedProfitPercent = prev.hedgeBalancedProfitPercent;

      if (prev.exchange1.position.actualPnlUsdt !== '---' && prev.exchange2.position.actualPnlUsdt !== '---') {
        const ex1PnlValue = typeof prev.exchange1.position.actualPnlUsdt === 'number' ? prev.exchange1.position.actualPnlUsdt : parseFloat(prev.exchange1.position.actualPnlUsdt);
        const ex2PnlValue = typeof prev.exchange2.position.actualPnlUsdt === 'number' ? prev.exchange2.position.actualPnlUsdt : parseFloat(prev.exchange2.position.actualPnlUsdt);
        const ex1FundingValue = ex1Fundings !== 'N/A' ? ex1Total : 0;
        const ex2FundingValue = ex2Fundings !== 'N/A' ? ex2Total : 0;

        hedgeBalancedProfitUsdt = ex1PnlValue + ex2PnlValue + ex1FundingValue + ex2FundingValue;

        const ex1PercentValue = typeof prev.exchange1.position.actualPnl === 'number' ? prev.exchange1.position.actualPnl : parseFloat(prev.exchange1.position.actualPnl);
        const ex2PercentValue = typeof prev.exchange2.position.actualPnl === 'number' ? prev.exchange2.position.actualPnl : parseFloat(prev.exchange2.position.actualPnl);
        hedgeBalancedProfitPercent = ex1PercentValue + ex2PercentValue;
      }

      return {
        ...prev,
        exchange1: {
          ...prev.exchange1,
          position: {
            ...prev.exchange1.position,
            actualRealizedFundings: ex1Fundings
          }
        },
        exchange2: {
          ...prev.exchange2,
          position: {
            ...prev.exchange2.position,
            actualRealizedFundings: ex2Fundings
          }
        },
        hedgeBalancedProfit: hedgeBalancedProfitUsdt !== '---' ?
          `${formatForDisplay(hedgeBalancedProfitUsdt)} USDT (${formatForDisplay(hedgeBalancedProfitPercent)} %)` : '---',
        hedgeBalancedProfitUsdt: hedgeBalancedProfitUsdt,
        hedgeBalancedProfitPercent: hedgeBalancedProfitPercent
      };
    });
  };

  // eslint-disable-next-line no-unused-vars
  const loadTradeDetails = async (tradeId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/trades/${tradeId}`);
      const data = await response.json();

      if (data.success) {
        const trade = data.data;

        // Fetch funding fees and taker/maker fees based on contract type
        let exchange1FundingFee = '---';
        let exchange2FundingFee = '---';
        let exchange1TakerFee = '---';
        let exchange1MakerFee = '---';
        let exchange2TakerFee = '---';
        let exchange2MakerFee = '---';

        const contract1 = (trade.exchange1_contract || '').toUpperCase();
        const contract2 = (trade.exchange2_contract || '').toUpperCase();

        // Fetch fees for exchange 1
        if (contract1 === 'SPOT') {
          // Set funding fee to N/A for SPOT contracts
          exchange1FundingFee = 'N/A';

          // Fetch taker/maker fees for SPOT from category='spot'
          try {
            const feesUrl = `${API_BASE_URL}/api/taker-maker-fees?exchangeId=${trade.exchange1_id}&symbolId=${trade.symbol_id}`;
            // console.error('Fetching taker-maker fees URL:', feesUrl);
            const feesResponse = await fetch(feesUrl);

            const feesData = await feesResponse.json();
            if (feesData.success && feesData.data.length > 0) {
              const spotFee = feesData.data.find(f => f.category && f.category.toLowerCase() === 'spot');
              if (spotFee) {
                exchange1TakerFee = spotFee.taker_fee != null ? (spotFee.taker_fee * 100).toFixed(4) : '---';
                exchange1MakerFee = spotFee.maker_fee != null ? (spotFee.maker_fee * 100).toFixed(4) : '---';
              }
            }
          } catch (error) {
            console.error('Error fetching spot fees for exchange 1:', error);
          }
        } else if (contract1 === 'LONG' || contract1 === 'SHORT') {
          // Fetch funding fee for LONG/SHORT
          try {
            const fundingResponse = await fetch(
              `${API_BASE_URL}/api/funding-fees?exchangeId=${trade.exchange1_id}&symbolId=${trade.symbol_id}`
            );
            const fundingData = await fundingResponse.json();
            if (fundingData.success && fundingData.data.length > 0) {
              const rate = fundingData.data[0].funding_rate;
              exchange1FundingFee = (rate != null) ? (rate * 100).toFixed(4) : '---';
            }
          } catch (error) {
            console.error('Error fetching funding fee for exchange 1: exchange1FundingFee=', exchange1FundingFee);
          }

          // Fetch taker/maker fees for LONG/SHORT from category='futures' or 'perpetual'
          try {
            const feesUrl = `${API_BASE_URL}/api/taker-maker-fees?exchangeId=${trade.exchange1_id}&symbolId=${trade.symbol_id}`;
            // console.info('Fetching taker-maker fees for LONG/SHORT position URL:', feesUrl);
            const feesResponse = await fetch(feesUrl);
            const feesData = await feesResponse.json();

            if (feesData.success && feesData.data.length > 0) {
              const futuresFee = feesData.data.find(f =>
                f.category && (f.category.toLowerCase() === 'futures' || f.category.toLowerCase() === 'perpetual' || f.category.toLowerCase() === 'inverse')
              );
              if (futuresFee) {
                exchange1TakerFee = futuresFee.taker_fee != null ? (futuresFee.taker_fee * 100).toFixed(4) : '---';
                exchange1MakerFee = futuresFee.maker_fee != null ? (futuresFee.maker_fee * 100).toFixed(4) : '---';
                // console.error('Found futures taker maker fees:', JSON.stringify(feesData, null, 2));
              } else {
                console.error('Expecting futures taker maker fee, but found other categories. Probably spot.');
              }
            }
          } catch (error) {
            console.error('Error fetching futures fees for exchange 1:', error);
          }
        }

        // Fetch fees for exchange 2
        if (contract2 === 'SPOT') {
          // Set funding fee to N/A for SPOT contracts
          exchange2FundingFee = 'N/A';

          // Fetch taker/maker fees for SPOT from category='spot'
          try {
            const feesResponse = await fetch(
              `${API_BASE_URL}/api/taker-maker-fees?exchangeId=${trade.exchange2_id}&symbolId=${trade.symbol_id}`
            );
            const feesData = await feesResponse.json();
            if (feesData.success && feesData.data.length > 0) {
              const spotFee = feesData.data.find(f => f.category && f.category.toLowerCase() === 'spot');
              if (spotFee) {
                exchange2TakerFee = spotFee.taker_fee != null ? (spotFee.taker_fee * 100).toFixed(4) : '---';
                exchange2MakerFee = spotFee.maker_fee != null ? (spotFee.maker_fee * 100).toFixed(4) : '---';
              }
            }
          } catch (error) {
            console.error('Error fetching spot fees for exchange 2:', error);
          }
        } else if (contract2 === 'LONG' || contract2 === 'SHORT') {
          // Fetch funding fee for LONG/SHORT
          try {
            const fundingResponse = await fetch(
              `${API_BASE_URL}/api/funding-fees?exchangeId=${trade.exchange2_id}&symbolId=${trade.symbol_id}`
            );
            const fundingData = await fundingResponse.json();
            if (fundingData.success && fundingData.data.length > 0) {
              const rate = fundingData.data[0].funding_rate;
              exchange2FundingFee = (rate != null) ? (rate * 100).toFixed(4) : '---';
            }
          } catch (error) {
            console.error('Error fetching funding fee for exchange 2:', error);
          }

          // Fetch taker/maker fees for LONG/SHORT from category='futures' or 'perpetual'
          try {
            const feesResponse = await fetch(
              `${API_BASE_URL}/api/taker-maker-fees?exchangeId=${trade.exchange2_id}&symbolId=${trade.symbol_id}`
            );
            const feesData = await feesResponse.json();
            if (feesData.success && feesData.data.length > 0) {
              const futuresFee = feesData.data.find(f =>
                f.category && (f.category.toLowerCase() === 'futures' || f.category.toLowerCase() === 'perpetual'  || f.category.toLowerCase() === 'inverse')
              );
              if (futuresFee) {
                exchange2TakerFee = futuresFee.taker_fee != null ? (futuresFee.taker_fee * 100).toFixed(4) : '---';
                exchange2MakerFee = futuresFee.maker_fee != null ? (futuresFee.maker_fee * 100).toFixed(4) : '---';
              }
            }
          } catch (error) {
            console.error('Error fetching futures fees for exchange 2:', error);
          }
        }

        // Store exchange IDs, symbolId, and contracts for periodic orderbook fetching
        setExchange1Id(trade.exchange1_id);
        setExchange2Id(trade.exchange2_id);
        setSymbolId(trade.symbol_id);
        setExchange1Contract(contract1);
        setExchange2Contract(contract2);

        // Get position opening prices, volumes and fees
        const position1OpeningPrice = trade.position1_opening_price;
        const position2OpeningPrice = trade.position2_opening_price;
        const position1OpeningVolume = trade.position1_opening_volume;
        const position2OpeningVolume = trade.position2_opening_volume;
        const position1OpeningFee = trade.position1_opening_fee;
        const position2OpeningFee = trade.position2_opening_fee;

        // Determine if positions are open or closed
        const position1IsOpenOrClosed = (trade.position1_status === 'open' || trade.position1_status === 'closed');
        const position2IsOpenOrClosed = (trade.position2_status === 'open' || trade.position2_status === 'closed');

        // Set volume from DB if position is open or closed
        if (position1IsOpenOrClosed && position1OpeningVolume) {
          setVolume1(position1OpeningVolume.toString());
        } else {
          setVolume1('');
        }

        if (position2IsOpenOrClosed && position2OpeningVolume) {
          setVolume2(position2OpeningVolume.toString());
        } else {
          setVolume2('');
        }

        // Format "Position opened at" - only show for open or closed positions
        const position1OpenedAt = position1IsOpenOrClosed && position1OpeningPrice
          ? formatNumberAsString(parseFloat(position1OpeningPrice).toFixed(2))
          : '---';
        const position2OpenedAt = position2IsOpenOrClosed && position2OpeningPrice
          ? formatNumberAsString(parseFloat(position2OpeningPrice).toFixed(2))
          : '---';

        // Calculate "Paid for position" = realized_opening_price * (1 + realized_opening_fee) * realized_opening_volume
        const position1PaidForPosition = position1IsOpenOrClosed && position1OpeningPrice && position1OpeningFee && position1OpeningVolume
          ? formatNumberAsString((parseFloat(position1OpeningPrice) * (1 + parseFloat(position1OpeningFee)) * parseFloat(position1OpeningVolume)).toFixed(2))
          : '---';
        const position2PaidForPosition = position2IsOpenOrClosed && position2OpeningPrice && position2OpeningFee && position2OpeningVolume
          ? formatNumberAsString((parseFloat(position2OpeningPrice) * (1 + parseFloat(position2OpeningFee)) * parseFloat(position2OpeningVolume)).toFixed(2))
          : '---';

        // Calculate "Opening paid fee" in % and USDT
        // Fee in % (convert from decimal to percentage)
        const position1OpeningFeePercent = position1IsOpenOrClosed && position1OpeningFee
          ? (parseFloat(position1OpeningFee) * 100).toFixed(4)
          : '---';
        const position2OpeningFeePercent = position2IsOpenOrClosed && position2OpeningFee
          ? (parseFloat(position2OpeningFee) * 100).toFixed(4)
          : '---';

        // Fee in USDT = realized_opening_price * realized_opening_volume * realized_opening_fee
        const position1OpeningPaidFeeUsdt = position1IsOpenOrClosed && position1OpeningPrice && position1OpeningVolume && position1OpeningFee
          ? formatNumberAsString((parseFloat(position1OpeningPrice) * parseFloat(position1OpeningVolume) * parseFloat(position1OpeningFee)).toFixed(2))
          : '---';
        const position2OpeningPaidFeeUsdt = position2IsOpenOrClosed && position2OpeningPrice && position2OpeningVolume && position2OpeningFee
          ? formatNumberAsString((parseFloat(position2OpeningPrice) * parseFloat(position2OpeningVolume) * parseFloat(position2OpeningFee)).toFixed(2))
          : '---';

        // Get closing data from DB
        const position1ClosingPrice = trade.position1_closing_price;
        const position2ClosingPrice = trade.position2_closing_price;
        const position1ClosingFee = trade.position1_closing_fee;
        const position2ClosingFee = trade.position2_closing_fee;

        // Determine if positions are closed
        const position1IsClosed = (trade.position1_status === 'closed');
        const position2IsClosed = (trade.position2_status === 'closed');

        // Calculate "Position closed at" - only show for closed positions
        const position1ClosedAt = position1IsClosed && position1ClosingPrice
          ? formatNumberAsString(parseFloat(position1ClosingPrice).toFixed(2))
          : '---';
        const position2ClosedAt = position2IsClosed && position2ClosingPrice
          ? formatNumberAsString(parseFloat(position2ClosingPrice).toFixed(2))
          : '---';

        // Calculate "Closing paid fee" in % and USDT
        // Fee in % (convert from decimal to percentage)
        const position1ClosingFeePercent = position1IsClosed && position1ClosingFee
          ? (parseFloat(position1ClosingFee) * 100).toFixed(4)
          : '---';
        const position2ClosingFeePercent = position2IsClosed && position2ClosingFee
          ? (parseFloat(position2ClosingFee) * 100).toFixed(4)
          : '---';

        // Fee in USDT = realized_closing_fee * realized_closing_price * realized_opening_volume
        const position1ClosingPaidFeeUsdt = position1IsClosed && position1ClosingFee && position1ClosingPrice && position1OpeningVolume
          ? formatNumberAsString((parseFloat(position1ClosingFee) * parseFloat(position1ClosingPrice) * parseFloat(position1OpeningVolume)).toFixed(2))
          : '---';
        const position2ClosingPaidFeeUsdt = position2IsClosed && position2ClosingFee && position2ClosingPrice && position2OpeningVolume
          ? formatNumberAsString((parseFloat(position2ClosingFee) * parseFloat(position2ClosingPrice) * parseFloat(position2OpeningVolume)).toFixed(2))
          : '---';

        // Calculate "Closed position for" = realized_closing_price * realized_opening_volume + closing paid fee in USDT
        const position1ClosedPositionFor = position1IsClosed && position1ClosingPrice && position1OpeningVolume && position1ClosingPaidFeeUsdt !== '---'
          ? formatNumberAsString((parseFloat(position1ClosingPrice) * parseFloat(position1OpeningVolume) + parseFloat(position1ClosingPaidFeeUsdt.replace(/\s/g, ''))).toFixed(2))
          : '---';
        const position2ClosedPositionFor = position2IsClosed && position2ClosingPrice && position2OpeningVolume && position2ClosingPaidFeeUsdt !== '---'
          ? formatNumberAsString((parseFloat(position2ClosingPrice) * parseFloat(position2OpeningVolume) + parseFloat(position2ClosingPaidFeeUsdt.replace(/\s/g, ''))).toFixed(2))
          : '---';

        // Update trade view with actual data from DB
        setTradeData({
          tradeId: trade.trade_id || '---',
          tradeStatus: trade.status || '---',
          realizedProfitPercent: trade.realized_profit_percent != null ? trade.realized_profit_percent.toFixed(2) : '---',
          realizedProfitUsdt: trade.realized_profit_usdt != null ? trade.realized_profit_usdt.toFixed(2) : '---',
          exchange1: {
            name: trade.exchange1_name || '---',
            symbol: trade.symbol || trade.symbol_name || '---',
            contract: contract1 || '---',
            positionId: trade.position1_id || '---',
            fundingFee: exchange1FundingFee,
            takerFee: exchange1TakerFee,
            makerFee: exchange1MakerFee,
            orderBook: {
              bestSell: '---',
              actualPrice: '---',
              bestBuy: '---',
              takerPrice: '---',
              makerPrice: '---',
              depthToMove: '---',
              depthToMoveVolume: '---'
            },
            position: {
              status: trade.position1_status || 'pending',
              opened: position1IsOpenOrClosed,
              openedAt: position1OpenedAt,
              openingTimestamp: formatTimestamp(trade.position1_opening_timestamp),
              openingPriceRaw: position1OpeningPrice, // Store raw value for PNL calculation
              openingVolumeRaw: position1OpeningVolume, // Store raw value for PNL calculation
              openingFee: position1OpeningFee || '---',
              openingFeePercent: position1OpeningFeePercent,
              openingPaidFeeUsdt: position1OpeningPaidFeeUsdt,
              paidForPosition: position1PaidForPosition,
              closedAt: position1ClosedAt,
              closingFee: position1ClosingFee || '---',
              closingFeePercent: position1ClosingFeePercent,
              closingPaidFeeUsdt: position1ClosingPaidFeeUsdt,
              closedPositionFor: position1ClosedPositionFor,
              actualPnl: '---', // Will be calculated after orderbook data is fetched
              actualPnlUsdt: '---' // Will be calculated after orderbook data is fetched
            }
          },
          exchange2: {
            name: trade.exchange2_name || '---',
            symbol: trade.symbol || trade.symbol_name || '---',
            contract: contract2 || '---',
            positionId: trade.position2_id || '---',
            fundingFee: exchange2FundingFee,
            takerFee: exchange2TakerFee,
            makerFee: exchange2MakerFee,
            orderBook: {
              bestSell: '---',
              actualPrice: '---',
              bestBuy: '---',
              takerPrice: '---',
              makerPrice: '---',
              depthToMove: '---',
              depthToMoveVolume: '---'
            },
            position: {
              status: trade.position2_status || 'pending',
              opened: position2IsOpenOrClosed,
              openedAt: position2OpenedAt,
              openingTimestamp: formatTimestamp(trade.position2_opening_timestamp),
              openingPriceRaw: position2OpeningPrice, // Store raw value for PNL calculation
              openingVolumeRaw: position2OpeningVolume, // Store raw value for PNL calculation
              openingFee: position2OpeningFee || '---',
              openingFeePercent: position2OpeningFeePercent,
              openingPaidFeeUsdt: position2OpeningPaidFeeUsdt,
              paidForPosition: position2PaidForPosition,
              closedAt: position2ClosedAt,
              closingFee: position2ClosingFee || '---',
              closingFeePercent: position2ClosingFeePercent,
              closingPaidFeeUsdt: position2ClosingPaidFeeUsdt,
              closedPositionFor: position2ClosedPositionFor,
              actualPnl: '---', // Will be calculated after orderbook data is fetched
              actualPnlUsdt: '---' // Will be calculated after orderbook data is fetched
            }
          },
          hedgeBalancedProfit: '---',
          finalProfits: {
            ex1Taker_ex2Taker: '---',
            ex1Taker_ex2Maker: '---',
            ex1Maker_ex2Taker: '---',
            ex1Maker_ex2Maker: '---'
          }
        });

        // Fetch fundings for this trade
        fetchFundings(tradeId);
      }
    } catch (error) {
      console.error('Error loading trade details:', error);
    }
  };


  // Handle volume input change for Exchange 1
  const handleVolume1Change = (e) => {
    const value = e.target.value;
    // Allow only numbers and decimal point
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setVolume1(value);
    }
  };

  // Handle volume input change for Exchange 2
  const handleVolume2Change = (e) => {
    const value = e.target.value;
    // Allow only numbers and decimal point
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setVolume2(value);
    }
  };

  // Calculate total price based on volume and unit price
  const calculateTotalPrice = (volume, unitPrice) => {
    if (!volume || volume === '' || unitPrice === '---') {
      return '---';
    }
    const volumeNum = parseFloat(volume);
    const priceNum = typeof unitPrice === 'number' ? unitPrice : parseFloat(unitPrice);
    if (isNaN(volumeNum) || isNaN(priceNum) || volumeNum === 0) {
      return '---';
    }
    return volumeNum * priceNum;
  };

  // Handle trade deletion
  const handleDeleteTrade = async (tradeId, e) => {
    // Stop event propagation to prevent row click
    e.stopPropagation();

    // Confirm deletion
    const confirmDelete = window.confirm(
      `Are you sure you want to delete trade #${tradeId}?\n\nThis will permanently delete the trade and all associated positions.`
    );

    if (!confirmDelete) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/trades/${tradeId}`, {
        method: 'DELETE'
      });

      const result = await response.json();

      if (result.success) {
        console.log(`Trade ${tradeId} deleted successfully`);
        //alert(`Trade #${tradeId} has been deleted successfully.`);

        // Refresh the trades list
        fetchTrades();

      } else {
        console.error('Failed to delete trade:', result.error);
        alert(`Failed to delete trade: ${result.error}`);
      }
    } catch (error) {
      console.error('Error deleting trade:', error);
      alert(`Error deleting trade: ${error.message}`);
    }
  };

  // Handle checkbox toggle for individual trade
  const handleToggleSelectTrade = (tradeId, e) => {
    e.stopPropagation(); // Prevent row click
    setSelectedTradeIds(prev => {
      if (prev.includes(tradeId)) {
        return prev.filter(id => id !== tradeId);
      } else {
        return [...prev, tradeId];
      }
    });
  };

  // Handle select/deselect all trades
  const handleSelectAllTrades = (e) => {
    if (e.target.checked) {
      // Select all trades
      setSelectedTradeIds(trades.map(trade => trade.trade_id));
    } else {
      // Deselect all
      setSelectedTradeIds([]);
    }
  };

  // Toggle mobile row - navigate to trade detail page
  const toggleMobileRow = (tradeId) => {
    navigate(`/trades/${tradeId}`);
  };

  // Handle press start (for visual feedback on all three rows)
  const handlePressStart = (tradeId, e) => {
    e.stopPropagation();
    setPressingTradeId(tradeId);
  };

  // Handle press end (remove visual feedback)
  const handlePressEnd = (e) => {
    if (e) e.stopPropagation();
    setPressingTradeId(null);
  };

  // Handle bulk action dropdown change
  const handleBulkActionChange = (e) => {
    setBulkAction(e.target.value);
  };

  // Execute the selected bulk action
  const handleExecuteBulkAction = async () => {
    if (selectedTradeIds.length === 0) {
      alert('Please select at least one trade.');
      return;
    }

    if (!bulkAction) {
      alert('Please select an action.');
      return;
    }

    if (bulkAction === 'delete') {
      // Confirm bulk deletion
      const confirmDelete = window.confirm(
        `Are you sure you want to delete ${selectedTradeIds.length} trade(s)?\n\nThis will permanently delete the selected trades and all associated positions.`
      );

      if (!confirmDelete) {
        return;
      }

      try {
        // Delete each trade sequentially
        let successCount = 0;
        let failCount = 0;

        for (const tradeId of selectedTradeIds) {
          try {
            const response = await fetch(`${API_BASE_URL}/api/trades/${tradeId}`, {
              method: 'DELETE'
            });

            const result = await response.json();

            if (result.success) {
              successCount++;
              console.log(`Trade ${tradeId} deleted successfully`);
            } else {
              failCount++;
              console.error(`Failed to delete trade ${tradeId}:`, result.error);
            }
          } catch (error) {
            failCount++;
            console.error(`Error deleting trade ${tradeId}:`, error);
          }
        }

        // Show result
        if (failCount === 0) {
          alert(`Successfully deleted ${successCount} trade(s).`);
        } else {
          alert(`Deleted ${successCount} trade(s).\nFailed to delete ${failCount} trade(s).`);
        }

        // Clear bulk action and refresh (keep checkboxes selected)
        setBulkAction('');
        fetchTrades();

      } catch (error) {
        console.error('Error executing bulk delete:', error);
        alert(`Error executing bulk delete: ${error.message}`);
      }
    } else if (bulkAction === 'create_trade_job') {
      // Open the bulk trade job dialog with current date and time prefilled
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const currentDateTime = `${year}-${month}-${day}T${hours}:${minutes}`;

      setBulkTradeJobDialogData(prev => ({
        ...prev,
        scheduledTime: currentDateTime
      }));

      setShowBulkTradeJobDialog(true);
    }
  };

  // Handle bulk trade job dialog change
  const handleBulkTradeJobDialogChange = (field, value) => {
    // When actionType is set to "CLOSE-AUTOMATIC", prefill scheduledTime with next funding time - 5 minutes
    if (field === 'actionType' && value === 'CLOSE-AUTOMATIC') {
      // Calculate next funding time
      const nextFunding = getNextFundingTime();

      // Subtract 5 minutes
      const scheduledDate = new Date(nextFunding);
      scheduledDate.setMinutes(scheduledDate.getMinutes() - 5);

      // Format for datetime-local input (YYYY-MM-DDTHH:MM)
      const year = scheduledDate.getFullYear();
      const month = String(scheduledDate.getMonth() + 1).padStart(2, '0');
      const day = String(scheduledDate.getDate()).padStart(2, '0');
      const hours = String(scheduledDate.getHours()).padStart(2, '0');
      const minutes = String(scheduledDate.getMinutes()).padStart(2, '0');
      const scheduledTime = `${year}-${month}-${day}T${hours}:${minutes}`;

      setBulkTradeJobDialogData(prev => ({
        ...prev,
        [field]: value,
        scheduledTime: scheduledTime
      }));
    } else {
      setBulkTradeJobDialogData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  // Handle bulk trade job dialog submit
  const handleConfirmBulkTradeJob = async () => {
    try {
      // Validate required fields
      if (!bulkTradeJobDialogData.volume || !bulkTradeJobDialogData.scheduledTime) {
        alert('Please fill in all required fields: Volume and Scheduled Time');
        return;
      }

      // Create jobs for all selected trades
      let successCount = 0;
      let failCount = 0;
      const errors = [];

      for (const tradeId of selectedTradeIds) {
        try {
          // Prepare the job data
          const jobData = {
            target_item_id: tradeId,
            target_item_type: 'TRADE',
            action_type: bulkTradeJobDialogData.actionType,
            trigger_type: bulkTradeJobDialogData.triggerType,
            condition: bulkTradeJobDialogData.condition,
            volume: parseFloat(bulkTradeJobDialogData.volume),
            scheduled_time: bulkTradeJobDialogData.scheduledTime,
            condition_value: bulkTradeJobDialogData.tradeProfit ? parseFloat(bulkTradeJobDialogData.tradeProfit) / 100 : null,
            status: 'SCHEDULED'
          };

          console.log(`Creating trade job for trade ${tradeId}:`, jobData);

          const response = await fetch(`${API_BASE_URL}/api/trade-position-jobs`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(jobData)
          });

          const data = await response.json();

          if (data.success) {
            successCount++;
            console.log(`Trade job created successfully for trade ${tradeId}`);
          } else {
            failCount++;
            errors.push(`Trade ${tradeId}: ${data.error || 'Unknown error'}`);
          }
        } catch (error) {
          failCount++;
          errors.push(`Trade ${tradeId}: ${error.message}`);
          console.error(`Error creating trade job for trade ${tradeId}:`, error);
        }
      }

      // Show result
      let message = `Successfully created ${successCount} trade job(s).`;
      if (failCount > 0) {
        message += `\nFailed to create ${failCount} trade job(s).`;
        if (errors.length > 0) {
          message += `\n\nErrors:\n${errors.slice(0, 5).join('\n')}`;
          if (errors.length > 5) {
            message += `\n... and ${errors.length - 5} more errors`;
          }
        }
      }
      alert(message);

      // Close dialog and clear bulk action (keep checkboxes selected)
      setShowBulkTradeJobDialog(false);
      setBulkAction('');

      // Reset dialog data
      setBulkTradeJobDialogData({
        actionType: 'OPEN-TRADE',
        triggerType: 'TRADE_PROFIT',
        condition: '>',
        tradeProfit: '',
        volume: '',
        scheduledTime: ''
      });
    } catch (error) {
      console.error('Error executing bulk create trade job:', error);
      alert(`Error executing bulk create trade job: ${error.message}`);
    }
  };

  const handleClose = async (type, exchange) => {
    console.log(`Closing position with ${type} price for ${exchange}...`);

    try {
      // Determine which exchange data to use
      const isExchange1 = exchange === 'exchange1';
      const exchangeData = isExchange1 ? tradeData.exchange1 : tradeData.exchange2;
      const exchangeIdValue = isExchange1 ? exchange1Id : exchange2Id;

      // Determine if taker or maker
      const isTaker = type === 'taker' ? 1 : 0;

      // Get closing price based on type
      // For taker: use best ask (takerPrice without fee)
      // For maker: use actual price (makerPrice without fee)
      const closingPrice = isTaker
        ? exchangeData.orderBook.bestBuy  // Best ask for closing
        : exchangeData.orderBook.actualPrice;

      // Validate closing price
      if (closingPrice === '---' || !closingPrice) {
        alert('Closing price is not available. Please refresh the orderbook data.');
        return;
      }

      // Prepare request body
      const requestBody = {
        position_id: exchangeData.positionId,
        exchange_id: exchangeIdValue,
        symbol_id: symbolId,
        taker: isTaker,
        closing_price: parseFloat(closingPrice)
      };

      console.log('Closing position with parameters:', requestBody);

      // Call API
      const response = await fetch(`${API_BASE_URL}/api/close-position-simulated`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      const result = await response.json();

      if (result.success) {
        console.log('Position closed successfully:', result.data);
        alert(`Position closed successfully!\nClosing Price: ${closingPrice} USDT\nFee: ${result.data.details.realized_closing_fee.toFixed(2)} USDT`);
      } else {
        console.error('Failed to close position:', result.error);
        alert(`Failed to close position: ${result.error}`);
      }
    } catch (error) {
      console.error('Error closing position:', error);
      alert(`Error closing position: ${error.message}`);
    }
  };

  const handleOpen = async (type, exchange) => {
    console.log(`Opening position with ${type} price for ${exchange}...`);

    try {
      // Determine which exchange data to use
      const isExchange1 = exchange === 'exchange1';
      const exchangeData = isExchange1 ? tradeData.exchange1 : tradeData.exchange2;
      const exchangeIdValue = isExchange1 ? exchange1Id : exchange2Id;
      const volume = isExchange1 ? volume1 : volume2;

      // Validate volume is entered
      if (!volume || volume === '' || parseFloat(volume) <= 0) {
        alert('Please enter a valid volume before opening the position.');
        return;
      }

      // Determine if taker or maker
      const isTaker = type === 'taker' ? 1 : 0;

      // Get opening price based on type
      const openingPrice = isTaker
        ? exchangeData.orderBook.takerPrice
        : exchangeData.orderBook.makerPrice;

      // Validate opening price
      if (openingPrice === '---' || !openingPrice) {
        alert('Opening price is not available. Please refresh the orderbook data.');
        return;
      }

      // Prepare request body
      const requestBody = {
        position_id: exchangeData.positionId,
        exchange_id: exchangeIdValue,
        symbol_id: symbolId,
        quantity: parseFloat(volume),
        taker: isTaker,
        opening_price: parseFloat(openingPrice)
      };

      console.log('Opening position with parameters:', requestBody);

      // Call API
      const response = await fetch(`${API_BASE_URL}/api/open-position-simulated`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      const result = await response.json();

      if (result.success) {
        console.log('Position opened successfully:', result.data);
        alert(`Position opened successfully!\nOpening Price: ${openingPrice} USDT\nVolume: ${volume}\nFee: ${result.data.details.realized_opening_fee.toFixed(2)} USDT`);
      } else {
        console.error('Failed to open position:', result.error);
        alert(`Failed to open position: ${result.error}`);
      }
    } catch (error) {
      console.error('Error opening position:', error);
      alert(`Error opening position: ${error.message}`);
    }
  };

  // Helper function to render action button based on position status
  const renderActionButton = (positionStatus, type, exchange) => {
    if (positionStatus === 'pending') {
      return (
        <button className="action-btn open-btn" onClick={() => handleOpen(type, exchange)}>
          Open
        </button>
      );
    } else if (positionStatus === 'open') {
      return (
        <button className="action-btn close-btn" onClick={() => handleClose(type, exchange)}>
          Close
        </button>
      );
    } else if (positionStatus === 'closed') {
      return (
        <span className="action-btn closed-btn">
          Closed
        </span>
      );
    }
  };

  return (
    <div className="page-content">
      <SEO
        title="Trade - simulated trading positions"
        description="Trade list - list of positions to simulate crypto trading funding fee strategy. Learn what to monitor, test how much you can earn from open hedged positions. Automate opening and closing positions."
        ogImage="https://micropincher.com/images/og-image.jpg"
        ogUrl="https://micropincher.com/trades"
        twitterImage="https://micropincher.com/images/og-image.jpg"
        canonicalUrl="https://micropincher.com/trades"
      />
      {/* Inline trade view hidden - trades now open in floating windows */}
      <div className="trades-container" style={{ display: 'none' }}>
        {/* Trade Header */}
        <div className="trade-header">
          <div className="trade-info-row">
            <span className="trade-label">Trade id:</span>
            <span className="trade-value">{tradeData.tradeId}</span>
          </div>
          <div className="trade-info-row">
            <span className="trade-label">Trade status:</span>
            <span className="trade-value">{formatTradeStatusCompact(tradeData.tradeStatus)}</span>
            <span className="trade-label"></span>
            <span className="trade-value"></span>
          </div>
          <div className="trade-info-row">
            <span className="trade-label">Trade realized profit:</span>
            <span className="trade-value">
              {tradeData.realizedProfitPercent}% {tradeData.realizedProfitUsdt} USDT
            </span>
            <span className="trade-label"></span>
            <span className="trade-value"></span>
          </div>
        </div>

        {/* Exchange Details Section */}
        <div className="section-divider">
          <div className="exchange-row">
            <span className="exchange-cell value" style={{textAlign: 'left'}} >Exchange X</span>
            <span className="exchange-cell value">&nbsp;</span>
            <span className="exchange-cell value" style={{textAlign: 'left'}} >Exchange 2</span>
            <span className="exchange-cell value">&nbsp;</span>
          </div>
        </div>

        <div className="exchange-details-grid">
          {/* Row: Exchange */}
          <div className="exchange-row">
            <span className="exchange-cell label">Exchange 1:</span>
            <span className="exchange-cell value">{tradeData.exchange1.name}</span>
            <span className="exchange-cell label">Exchange 2:</span>
            <span className="exchange-cell value">{tradeData.exchange2.name}</span>
          </div>

          {/* Row: Symbol */}
          <div className="exchange-row">
            <span className="exchange-cell label">Symbol:</span>
            <span className="exchange-cell value">{tradeData.exchange1.symbol}</span>
            <span className="exchange-cell label">Symbol:</span>
            <span className="exchange-cell value">{tradeData.exchange2.symbol}</span>
          </div>

          {/* Row: Contract */}
          <div className="exchange-row">
            <span className="exchange-cell label">Contract:</span>
            <span className="exchange-cell value">{tradeData.exchange1.contract}</span>
            <span className="exchange-cell label">Contract:</span>
            <span className="exchange-cell value">{tradeData.exchange2.contract}</span>
          </div>

          {/* Row: Position ID */}
          <div className="exchange-row">
            <span className="exchange-cell label">Position ID:</span>
            <span className="exchange-cell value">{tradeData.exchange1.positionId}</span>
            <span className="exchange-cell label">Position ID:</span>
            <span className="exchange-cell value">{tradeData.exchange2.positionId}</span>
          </div>

          {/* Row: Status */}
          <div className="exchange-row">
            <span className="exchange-cell label">Status:</span>
            <span className="exchange-cell value">{tradeData.exchange1.position.status}</span>
            <span className="exchange-cell label">Status:</span>
            <span className="exchange-cell value">{tradeData.exchange2.position.status}</span>
          </div>

          {/* Row: Funding Fee */}
          <div className="exchange-row">
            <span className="exchange-cell label">Funding fee:</span>
            <span className={`exchange-cell value ${getValueClass(tradeData.exchange1.fundingFee).includes('na-value') ? 'na-value' : ''}`}>{tradeData.exchange1.fundingFee}%</span>
            <span className="exchange-cell label">Funding fee:</span>
            <span className={`exchange-cell value ${getValueClass(tradeData.exchange2.fundingFee).includes('na-value') ? 'na-value' : ''}`}>{tradeData.exchange2.fundingFee}%</span>
          </div>

          {/* Row: Next Funding Time */}
          <div className="exchange-row">
            <span className="exchange-cell label">Next funding:</span>
            <span className="exchange-cell value" style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.2', padding: '2px 4px' }}>
              <div style={{ color: '#8b949e', fontSize: '11px' }}>
                {formatNextFundingTime(getNextFundingTime())}
              </div>
              <div style={{ color: formatCountdown(getNextFundingTime()).color, fontSize: '11px' }}>
                {formatCountdown(getNextFundingTime()).text}
              </div>
            </span>
            <span className="exchange-cell label">Next funding:</span>
            <span className="exchange-cell value" style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.2', padding: '2px 4px' }}>
              <div style={{ color: '#8b949e', fontSize: '11px' }}>
                {formatNextFundingTime(getNextFundingTime())}
              </div>
              <div style={{ color: formatCountdown(getNextFundingTime()).color, fontSize: '11px' }}>
                {formatCountdown(getNextFundingTime()).text}
              </div>
            </span>
          </div>

          {/* Row: Taker Fee */}
          <div className="exchange-row">
            <span className="exchange-cell label">Taker fee:</span>
            <span className={`exchange-cell value ${getValueClass(tradeData.exchange1.takerFee).includes('na-value') ? 'na-value' : ''}`}>{tradeData.exchange1.takerFee}%</span>
            <span className="exchange-cell label">Taker fee:</span>
            <span className={`exchange-cell value ${getValueClass(tradeData.exchange2.takerFee).includes('na-value') ? 'na-value' : ''}`}>{tradeData.exchange2.takerFee}%</span>
          </div>

          {/* Row: Maker Fee */}
          <div className="exchange-row">
            <span className="exchange-cell label">Maker fee:</span>
            <span className={`exchange-cell value ${getValueClass(tradeData.exchange1.makerFee).includes('na-value') ? 'na-value' : ''}`}>{tradeData.exchange1.makerFee}%</span>
            <span className="exchange-cell label">Maker fee:</span>
            <span className={`exchange-cell value ${getValueClass(tradeData.exchange2.makerFee).includes('na-value') ? 'na-value' : ''}`}>{tradeData.exchange2.makerFee}%</span>
          </div>

          {/* Row: Best bid */}
          <div className="exchange-row">
            <span className="exchange-cell label">Best bid:</span>
            <span className="exchange-cell value best-bid-value">{formatForDisplay(tradeData.exchange1.orderBook.bestSell)} USDT</span>
            <span className="exchange-cell label">Best bid:</span>
            <span className="exchange-cell value best-bid-value">{formatForDisplay(tradeData.exchange2.orderBook.bestSell)} USDT</span>
          </div>

          {/* Row: Actual price */}
          <div className="exchange-row">
            <span className="exchange-cell label">Actual price:</span>
            <span className="exchange-cell value">{formatForDisplay(tradeData.exchange1.orderBook.actualPrice)} USDT</span>
            <span className="exchange-cell label">Actual price:</span>
            <span className="exchange-cell value">{formatForDisplay(tradeData.exchange2.orderBook.actualPrice)} USDT</span>
          </div>

          {/* Row: Depth To Move (DTM 0.1%) */}
          <div className="exchange-row">
            <span className="exchange-cell label">Depth To Move (DTM 0.1%):</span>
            <span className="exchange-cell value">{formatNumberAsString(tradeData.exchange1.orderBook.depthToMove)} USDT</span>
            <span className="exchange-cell label">Depth To Move (DTM 0.1%):</span>
            <span className="exchange-cell value">{formatNumberAsString(tradeData.exchange2.orderBook.depthToMove)} USDT</span>
          </div>

          {/* Row: Depth To Move (DTM 0.1%) volume */}
          <div className="exchange-row">
            <span className="exchange-cell label">Depth To Move (DTM 0.1%) volume:</span>
            <span className="exchange-cell value">{formatNumberAsString(tradeData.exchange1.orderBook.depthToMoveVolume)} {tradeData.exchange1.symbol}</span>
            <span className="exchange-cell label">Depth To Move (DTM 0.1%) volume:</span>
            <span className="exchange-cell value">{formatNumberAsString(tradeData.exchange2.orderBook.depthToMoveVolume)} {tradeData.exchange1.symbol}</span>
          </div>

          {/* Row: Best ask */}
          <div className="exchange-row">
            <span className="exchange-cell label">Best ask:</span>
            <span className="exchange-cell value best-ask-value">{formatForDisplay(tradeData.exchange1.orderBook.bestBuy)} USDT</span>
            <span className="exchange-cell label">Best ask:</span>
            <span className="exchange-cell value best-ask-value">{formatForDisplay(tradeData.exchange2.orderBook.bestBuy)} USDT</span>
          </div>

          {/* Empty row */}
          <div className="exchange-row" style={{height: '4px'}}>
            <span className="exchange-cell"></span>
            <span className="exchange-cell"></span>
            <span className="exchange-cell"></span>
            <span className="exchange-cell"></span>
          </div>

          {/* Row: Taker price */}
          <div className="exchange-row">
            <span className="exchange-cell label">Taker price for 1  {tradeData.exchange1.symbol}:</span>
            <span className="exchange-cell value">{formatForDisplay(tradeData.exchange1.orderBook.takerPrice)} USDT</span>
            <span className="exchange-cell label">Taker price for 1  {tradeData.exchange1.symbol}:</span>
            <span className="exchange-cell value">{formatForDisplay(tradeData.exchange2.orderBook.takerPrice)} USDT</span>
          </div>

          {/* Row: Maker price */}
          <div className="exchange-row">
            <span className="exchange-cell label">Maker price for 1  {tradeData.exchange1.symbol}:</span>
            <span className="exchange-cell value">{formatForDisplay(tradeData.exchange1.orderBook.makerPrice)} USDT</span>
            <span className="exchange-cell label">Maker price for 1  {tradeData.exchange1.symbol}:</span>
            <span className="exchange-cell value">{formatForDisplay(tradeData.exchange2.orderBook.makerPrice)} USDT</span>
          </div>

          {/* Empty row */}
          <div className="exchange-row" style={{height: '4px'}}>
            <span className="exchange-cell"></span>
            <span className="exchange-cell"></span>
            <span className="exchange-cell"></span>
            <span className="exchange-cell"></span>
          </div>

          {/* Empty row before position section */}
          <div className="exchange-row" style={{height: '8px'}}>
            <span className="exchange-cell"></span>
            <span className="exchange-cell"></span>
            <span className="exchange-cell"></span>
            <span className="exchange-cell"></span>
          </div>

          {/* Empty rows */}
          <div className="exchange-row" style={{height: '4px'}}>
            <span className="exchange-cell"></span>
            <span className="exchange-cell"></span>
            <span className="exchange-cell"></span>
                <span className="exchange-cell"></span>
          </div>
          <div className="exchange-row" style={{height: '4px'}}>
            <span className="exchange-cell"></span>
            <span className="exchange-cell"></span>
            <span className="exchange-cell"></span>
            <span className="exchange-cell"></span>
          </div>

          {/* Row: Volume Input */}
          <div className="exchange-row">
            <span className="exchange-cell label">Volume:</span>
            <span className="exchange-cell value" style={{paddingRight: '5px'}}>
              <input
                type="text"
                value={volume1}
                onChange={handleVolume1Change}
                placeholder="0.00"
                disabled={tradeData.exchange1.position.status === 'open' || tradeData.exchange1.position.status === 'closed'}
                style={{
                  background: '#0d1117',
                  border: '1px solid #30363d',
                  borderRadius: '4px',
                  color: '#c9d1d9',
                  padding: '4px 8px',
                  fontFamily: "'Courier New', Courier, monospace",
                  fontSize: '12px',
                  width: '120px',
                  textAlign: 'right',
                  opacity: (tradeData.exchange1.position.status === 'open' || tradeData.exchange1.position.status === 'closed') ? 0.6 : 1,
                  cursor: (tradeData.exchange1.position.status === 'open' || tradeData.exchange1.position.status === 'closed') ? 'not-allowed' : 'text'
                }}
              />
            </span>
            <span className="exchange-cell label">Volume:</span>
            <span className="exchange-cell value" style={{paddingRight: '5px'}}>
              <input
                type="text"
                value={volume2}
                onChange={handleVolume2Change}
                placeholder="0.00"
                disabled={tradeData.exchange2.position.status === 'open' || tradeData.exchange2.position.status === 'closed'}
                style={{
                  background: '#0d1117',
                  border: '1px solid #30363d',
                  borderRadius: '4px',
                  color: '#c9d1d9',
                  padding: '4px 8px',
                  fontFamily: "'Courier New', Courier, monospace",
                  fontSize: '12px',
                  width: '120px',
                  textAlign: 'right',
                  opacity: (tradeData.exchange2.position.status === 'open' || tradeData.exchange2.position.status === 'closed') ? 0.6 : 1,
                  cursor: (tradeData.exchange2.position.status === 'open' || tradeData.exchange2.position.status === 'closed') ? 'not-allowed' : 'text'
                }}
              />
            </span>
          </div>

          {/* Row: Open/Close as taker */}
          <div className="exchange-row">
            <span className="exchange-cell label">
              {tradeData.exchange1.position.status === 'open' ? 'Close as taker:' : 'Open as taker:'}
            </span>
            <span className="exchange-cell value" style={{display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px', width: 'auto', whiteSpace: 'nowrap', paddingRight: '5px'}}>
              <span className={tradeData.exchange1.contract.toUpperCase() === 'SHORT' ? 'best-ask-value' : 'best-bid-value'}>
                {formatForDisplay(calculateTotalPrice(volume1, tradeData.exchange1.orderBook.takerPrice))} USDT
              </span>
              {renderActionButton(tradeData.exchange1.position.status, 'taker', 'exchange1')}
            </span>
            <span className="exchange-cell label">
              {tradeData.exchange2.position.status === 'open' ? 'Close as Taker:' : 'Open as taker:'}
            </span>
            <span className="exchange-cell value" style={{display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px', width: 'auto', whiteSpace: 'nowrap', paddingRight: '5px'}}>
              <span className={tradeData.exchange2.contract.toUpperCase() === 'SHORT' ? 'best-ask-value' : 'best-bid-value'}>
                {formatForDisplay(calculateTotalPrice(volume2, tradeData.exchange2.orderBook.takerPrice))} USDT
              </span>
              {renderActionButton(tradeData.exchange2.position.status, 'taker', 'exchange2')}
            </span>
          </div>

          {/* Row: Open/Close as maker */}
          <div className="exchange-row">
            <span className="exchange-cell label">
              {tradeData.exchange1.position.status === 'open' ? 'Close as maker:' : 'Open as maker:'}
            </span>
            <span className="exchange-cell value" style={{display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px', width: 'auto', whiteSpace: 'nowrap', paddingRight: '5px'}}>
              <span className={tradeData.exchange1.contract.toUpperCase() === 'SHORT' ? 'best-ask-value' : 'best-bid-value'}>
                {formatForDisplay(calculateTotalPrice(volume1, tradeData.exchange1.orderBook.makerPrice))} USDT
              </span>
              {renderActionButton(tradeData.exchange1.position.status, 'maker', 'exchange1')}
            </span>
            <span className="exchange-cell label">
              {tradeData.exchange2.position.status === 'open' ? 'Close as maker:' : 'Open as maker:'}
            </span>
            <span className="exchange-cell value" style={{display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px', width: 'auto', whiteSpace: 'nowrap', paddingRight: '5px'}}>
              <span className={tradeData.exchange2.contract.toUpperCase() === 'SHORT' ? 'best-ask-value' : 'best-bid-value'}>
                {formatForDisplay(calculateTotalPrice(volume2, tradeData.exchange2.orderBook.makerPrice))} USDT
              </span>
              {renderActionButton(tradeData.exchange2.position.status, 'maker', 'exchange2')}
            </span>
          </div>
          {/* Empty row */}
          <div className="exchange-row" style={{height: '8px'}}>
            <span className="exchange-cell"></span>
            <span className="exchange-cell"></span>
            <span className="exchange-cell"></span>
            <span className="exchange-cell"></span>
          </div>
          {/* Row: Position header */}
          <div className="exchange-row">
            <span className="exchange-cell label" style={{color: '#ffffff'}}>Position OPENED:</span>
            <span className="exchange-cell value">{tradeData.exchange1.position.openingTimestamp}</span>
            <span className="exchange-cell label" style={{color: '#ffffff'}}>Position OPENED:</span>
            <span className="exchange-cell value">{tradeData.exchange2.position.openingTimestamp}</span>
          </div>

          {/* Row: Position opened at */}
          <div className="exchange-row">
            <span className="exchange-cell label">Position opened at:</span>
            <span className="exchange-cell value">{tradeData.exchange1.position.openedAt} USDT</span>
            <span className="exchange-cell label">Position opened at:</span>
            <span className="exchange-cell value">{tradeData.exchange2.position.openedAt} USDT</span>
          </div>

          {/* Row: Opening paid fee */}
          <div className="exchange-row">
            <span className="exchange-cell label">Opening paid fee:</span>
            <span className="exchange-cell value">
              {tradeData.exchange1.position.openingFeePercent !== '---'
                ? `${tradeData.exchange1.position.openingFeePercent} % (${tradeData.exchange1.position.openingPaidFeeUsdt} USDT)`
                : '---'}
            </span>
            <span className="exchange-cell label">Opening paid fee:</span>
            <span className="exchange-cell value">
              {tradeData.exchange2.position.openingFeePercent !== '---'
                ? `${tradeData.exchange2.position.openingFeePercent} % (${tradeData.exchange2.position.openingPaidFeeUsdt} USDT)`
                : '---'}
            </span>
          </div>

          {/* Row: Paid for position */}
          <div className="exchange-row">
            <span className="exchange-cell label">Paid for position:</span>
            <span className="exchange-cell value">{tradeData.exchange1.position.paidForPosition} USDT</span>
            <span className="exchange-cell label">Paid for position:</span>
            <span className="exchange-cell value">{tradeData.exchange2.position.paidForPosition} USDT</span>
          </div>

          {/* Row: Position closed at */}
          <div className="exchange-row">
            <span className="exchange-cell label">Position closed at:</span>
            <span className="exchange-cell value">{tradeData.exchange1.position.closedAt} USDT</span>
            <span className="exchange-cell label">Position closed at:</span>
            <span className="exchange-cell value">{tradeData.exchange2.position.closedAt} USDT</span>
          </div>

          {/* Row: Closing paid fee */}
          <div className="exchange-row">
            <span className="exchange-cell label">Closing paid fee:</span>
            <span className="exchange-cell value">
              {tradeData.exchange1.position.closingFeePercent !== '---'
                ? `${tradeData.exchange1.position.closingFeePercent} % (${tradeData.exchange1.position.closingPaidFeeUsdt} USDT)`
                : '---'}
            </span>
            <span className="exchange-cell label">Closing paid fee:</span>
            <span className="exchange-cell value">
              {tradeData.exchange2.position.closingFeePercent !== '---'
                ? `${tradeData.exchange2.position.closingFeePercent} % (${tradeData.exchange2.position.closingPaidFeeUsdt} USDT)`
                : '---'}
            </span>
          </div>

          {/* Row: Closed position for */}
          <div className="exchange-row">
            <span className="exchange-cell label">Closed position for:</span>
            <span className="exchange-cell value">{tradeData.exchange1.position.closedPositionFor} USDT</span>
            <span className="exchange-cell label">Closed position for:</span>
            <span className="exchange-cell value">{tradeData.exchange2.position.closedPositionFor} USDT</span>
          </div>

          {/* Row: Actual position PNL */}
          <div className="exchange-row">
            <span className="exchange-cell label">Actual position PNL:</span>
            <span className="exchange-cell value" style={{
              color: tradeData.exchange1.position.actualPnlUsdt !== '---' && parseFloat(tradeData.exchange1.position.actualPnlUsdt) >= 0 ? '#3fb950' :
                     tradeData.exchange1.position.actualPnlUsdt !== '---' && parseFloat(tradeData.exchange1.position.actualPnlUsdt) < 0 ? '#f85149' : '#ffffff'
            }}>
              {tradeData.exchange1.position.actualPnlUsdt !== '---' ? `${formatForDisplay(tradeData.exchange1.position.actualPnlUsdt)} USDT (${formatForDisplay(tradeData.exchange1.position.actualPnl)} %)` : '---'}
            </span>
            <span className="exchange-cell label">Actual position PNL:</span>
            <span className="exchange-cell value" style={{
              color: tradeData.exchange2.position.actualPnlUsdt !== '---' && parseFloat(tradeData.exchange2.position.actualPnlUsdt) >= 0 ? '#3fb950' :
                     tradeData.exchange2.position.actualPnlUsdt !== '---' && parseFloat(tradeData.exchange2.position.actualPnlUsdt) < 0 ? '#f85149' : '#ffffff'
            }}>
              {tradeData.exchange2.position.actualPnlUsdt !== '---' ? `${formatForDisplay(tradeData.exchange2.position.actualPnlUsdt)} USDT (${formatForDisplay(tradeData.exchange2.position.actualPnl)} %)` : '---'}
            </span>
          </div>

          {/* Row: Actual realized fundings */}
          <div className="exchange-row">
            <span className="exchange-cell label">Actual realized fundings:</span>
            <span className={`exchange-cell value ${getValueClass(tradeData.exchange1.position.actualRealizedFundings).includes('na-value') ? 'na-value' : ''}`} style={{
              color: tradeData.exchange1.position.actualRealizedFundings !== '---' && tradeData.exchange1.position.actualRealizedFundings !== 'N/A' && parseFloat(tradeData.exchange1.position.actualRealizedFundings) >= 0 ? '#3fb950' :
                     tradeData.exchange1.position.actualRealizedFundings !== '---' && tradeData.exchange1.position.actualRealizedFundings !== 'N/A' && parseFloat(tradeData.exchange1.position.actualRealizedFundings) < 0 ? '#f85149' : '#ffffff'
            }}>
              {tradeData.exchange1.position.actualRealizedFundings !== '---' && tradeData.exchange1.position.actualRealizedFundings !== 'N/A' ? `${formatForDisplay(tradeData.exchange1.position.actualRealizedFundings)} USDT` : tradeData.exchange1.position.actualRealizedFundings}
            </span>
            <span className="exchange-cell label">Actual realized fundings:</span>
            <span className={`exchange-cell value ${getValueClass(tradeData.exchange2.position.actualRealizedFundings).includes('na-value') ? 'na-value' : ''}`} style={{
              color: tradeData.exchange2.position.actualRealizedFundings !== '---' && tradeData.exchange2.position.actualRealizedFundings !== 'N/A' && parseFloat(tradeData.exchange2.position.actualRealizedFundings) >= 0 ? '#3fb950' :
                     tradeData.exchange2.position.actualRealizedFundings !== '---' && tradeData.exchange2.position.actualRealizedFundings !== 'N/A' && parseFloat(tradeData.exchange2.position.actualRealizedFundings) < 0 ? '#f85149' : '#ffffff'
            }}>
              {tradeData.exchange2.position.actualRealizedFundings !== '---' && tradeData.exchange2.position.actualRealizedFundings !== 'N/A' ? `${formatForDisplay(tradeData.exchange2.position.actualRealizedFundings)} USDT` : tradeData.exchange2.position.actualRealizedFundings}
            </span>
          </div>

        </div>

        <div className="thick-divider"></div>

        {/* Final Profit Matrix */}
        <div className="final-profit-section">
          <div className="hedge-profit-section">
            <span className="hedge-profit-label">Hedge balanced profit:</span>
            <span className="hedge-profit-value" style={{
              color: tradeData.hedgeBalancedProfitUsdt !== '---' && parseFloat(tradeData.hedgeBalancedProfitUsdt) >= 0 ? '#3fb950' :
                     tradeData.hedgeBalancedProfitUsdt !== '---' && parseFloat(tradeData.hedgeBalancedProfitUsdt) < 0 ? '#f85149' : '#ffffff'
            }}>
              {tradeData.hedgeBalancedProfit}
            </span>
          </div>

          <h3 className="final-profit-title">Assigned fundings:</h3>
          <div className="profit-matrix">
            <table className="profit-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Ex1 position:</th>
                  <th>Ex1 funding rate:</th>
                  <th>Ex2 position:</th>
                  <th>Ex2 funding rate:</th>
                </tr>
              </thead>
              <tbody>
                {fundings.length === 0 ? (
                  <tr>
                    <td colSpan="5" style={{ textAlign: 'center', color: '#8b949e' }}>No funding data available</td>
                  </tr>
                ) : (
                  fundings.map((funding, index) => (
                    <tr key={index}>
                      <td className="row-label">{new Date(funding.timestamp).toLocaleString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}</td>
                      <td className="profit-cell">{funding.ex1_funding !== null ? `${formatForDisplay(funding.ex1_funding)} USDT` : '---'}</td>
                      <td className="profit-cell">{funding.ex1_funding_rate !== null && funding.ex1_funding_rate !== undefined ? `${(parseFloat(funding.ex1_funding_rate) * 100).toFixed(4)}%` : '---'}</td>
                      <td className="profit-cell">{funding.ex2_funding !== null ? `${formatForDisplay(funding.ex2_funding)} USDT` : '---'}</td>
                      <td className="profit-cell">{funding.ex2_funding_rate !== null && funding.ex2_funding_rate !== undefined ? `${(parseFloat(funding.ex2_funding_rate) * 100).toFixed(4)}%` : '---'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Trade List Section - Hidden, now in floating window */}
      <div className="trade-list-section" style={{ display: 'none' }}>
        <div className="trade-list-header">
          <h2 className="trade-list-title">Trade List</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label htmlFor="bulk-action-select-main" style={{ color: '#8b949e', fontSize: '14px' }}>
              Selected ({selectedTradeIds.length}):
            </label>
            <select
              id="bulk-action-select-main"
              value={bulkAction}
              onChange={handleBulkActionChange}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: '1px solid #30363d',
                background: '#0d1117',
                color: '#c9d1d9',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              <option value="">-- Select Action --</option>
              <option value="delete">Delete</option>
              <option value="create_trade_job">Create Trade Job</option>
            </select>
            <button
              className="btn-new-trade"
              onClick={handleExecuteBulkAction}
              disabled={selectedTradeIds.length === 0 || !bulkAction}
              style={{
                opacity: (selectedTradeIds.length === 0 || !bulkAction) ? 0.5 : 1,
                cursor: (selectedTradeIds.length === 0 || !bulkAction) ? 'not-allowed' : 'pointer'
              }}
            >
              Apply
            </button>
            <button className="btn-new-trade" onClick={() => setIsDialogOpen(true)}>
              Create New Trade
            </button>
          </div>
        </div>
      </div>

      {/* Render Trade List */}
      <div style={{ display: 'inline-block' }}>
        <div className="filters" style={{ marginBottom: '0', padding: '15px', backgroundColor: '#1a1b23', border: '1px solid #2a2d3a', borderRadius: '4px', fontFamily: "'Courier New', Courier, monospace" }}>
          {/* Top row - Refresh and Create buttons */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
            <button
              onClick={fetchTrades}
              style={{
                padding: '4px 8px',
                fontSize: '12px',
                fontFamily: "'Courier New', Courier, monospace",
                backgroundColor: '#238636',
                color: '#ffffff',
                border: '1px solid #238636',
                borderRadius: '3px',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              Refresh
            </button>
            <div style={{ flex: 1 }}></div>
            <button
              onClick={() => setIsDialogOpen(true)}
              style={{
                padding: '4px 8px',
                fontSize: '12px',
                fontFamily: "'Courier New', Courier, monospace",
                backgroundColor: '#238636',
                color: '#ffffff',
                border: '1px solid #238636',
                borderRadius: '3px',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              Create New Trade
            </button>
          </div>

          {/* Bottom row - Bulk actions */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label htmlFor="bulk-action-select" style={{ color: '#8b949e', fontSize: '12px' }}>
              Selected ({selectedTradeIds.length}):
            </label>
            <select
              id="bulk-action-select"
              value={bulkAction}
              onChange={handleBulkActionChange}
              style={{
                padding: '4px 8px',
                fontSize: '12px',
                fontFamily: "'Courier New', Courier, monospace",
                backgroundColor: '#21262d',
                border: '1px solid #2a2d3a',
                borderRadius: '3px',
                color: '#ffffff',
                cursor: 'pointer'
              }}
            >
              <option value="">-- Select Action --</option>
              <option value="delete">Delete</option>
              <option value="create_trade_job">Create Trade Job</option>
            </select>
            <div style={{ flex: 1 }}></div>
            <button
              className="btn-new-trade"
              onClick={handleExecuteBulkAction}
              disabled={selectedTradeIds.length === 0 || !bulkAction}
              style={{
                padding: '4px 8px',
                fontSize: '12px',
                fontFamily: "'Courier New', Courier, monospace",
                backgroundColor: (selectedTradeIds.length === 0 || !bulkAction) ? '#6c757d' : '#238636',
                color: '#ffffff',
                border: '1px solid #2a2d3a',
                borderRadius: '3px',
                cursor: (selectedTradeIds.length === 0 || !bulkAction) ? 'not-allowed' : 'pointer',
                opacity: (selectedTradeIds.length === 0 || !bulkAction) ? 0.6 : 1
              }}
            >
              Apply
            </button>
          </div>
        </div>

        {trades.length === 0 ? (
          <div className="trade-list-empty">No trades found. Create your first trade!</div>
        ) : (
          <div className="data-table">
          <table className="trade-list-table">
                  <thead>
                    <tr className={isMobileView ? 'mobile-header-line1' : 'desktop-header-line1'}>
                      <th rowSpan={isMobileView ? 3 : 2} style={{width: '30px', textAlign: 'center', verticalAlign: isMobileView ? 'middle' : 'middle'}}>
                        <input
                          type="checkbox"
                          checked={selectedTradeIds.length === trades.length && trades.length > 0}
                          onChange={handleSelectAllTrades}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </th>
                      <th className="hide-mobile" rowSpan={isMobileView ? 1 : 2} style={{verticalAlign: 'middle'}}>ID</th>
                      <th rowSpan={isMobileView ? 1 : 2} style={{verticalAlign: isMobileView ? 'middle' : 'top'}}>Name</th>
                      <th className="hide-mobile" rowSpan={isMobileView ? 1 : 1} style={{verticalAlign: 'bottom'}}>Symbol</th>
                      <th colSpan={isMobileView ? 1 : 1} style={{verticalAlign: isMobileView ? 'middle' : 'bottom'}}>Exch 1</th>
                      <th colSpan={isMobileView ? 1 : 1} style={{verticalAlign: isMobileView ? 'middle' : 'bottom'}}>Exch 2</th>
                      <th rowSpan={isMobileView ? 1 : 1} style={{verticalAlign: isMobileView ? 'middle' : 'bottom'}}>Reali. [USDT]</th>
                      <th className="hide-mobile" rowSpan={isMobileView ? 1 : 1} style={{verticalAlign: isMobileView ? 'middle' : 'bottom'}}>Status</th>
                      <th className="hide-mobile" rowSpan={isMobileView ? 1 : 1} style={{verticalAlign: 'bottom'}}>Trade Jobs</th>
                      <th className="hide-mobile" rowSpan={isMobileView ? 1 : 1} style={{verticalAlign: isMobileView ? 'middle' : 'top'}}>Created</th>
                      <th rowSpan={isMobileView ? 1 : 1} style={{ textAlign: 'left', verticalAlign: 'top', whiteSpace: 'nowrap'}} className="hide-mobile">Delete</th>
                    </tr>
                    {!isMobileView && (
                      <tr className="desktop-header-line2">
                        <th></th>
                        <th colSpan="1" style={{verticalAlign: 'top'}}>Contr 1</th>
                        <th colSpan="1" style={{verticalAlign: 'top'}}>Contr 2</th>
                        <th colSpan="1" style={{verticalAlign: 'top'}}>profit [%]</th>
                        <th colSpan="1" style={{verticalAlign: 'top'}}>[SCH/D/F]:</th>
                        <th></th>
                        <th></th>
                        <th></th>
                      </tr>
                    )}
                    {isMobileView && (
                      <tr className="mobile-header-line2">
                        <th style={{verticalAlign: 'top'}}>Symbol</th>
                        <th style={{verticalAlign: 'top'}}>Contract 1</th>
                        <th style={{verticalAlign: 'top'}}>Contract 2</th>
                        <th style={{verticalAlign: 'top'}}>Prof. [%]</th>
                      </tr>
                    )}
                    {isMobileView && (
                      <tr className="mobile-header-line3">
                        <th style={{verticalAlign: 'top'}}>Status</th>
                        <th style={{verticalAlign: 'top'}}>Created</th>
                        <th style={{verticalAlign: 'top'}}>C. Time</th>
                        <th style={{verticalAlign: 'top'}}>Trade Jobs</th>
                      </tr>
                    )}
                  </thead>
                  <tbody>
                    {trades.map(trade => {
                      const rowColor = getTradeRowColor(trade.status);
                      return (
                        <React.Fragment key={trade.trade_id}>
                        <tr
                          className={`trade-row ${isMobileView ? 'trade-row-mobile' : 'trade-row-desktop'} ${pressingTradeId === trade.trade_id ? 'pressing' : ''}`}
                          onClick={() => navigate(`/trades/${trade.trade_id}`)}
                          onMouseDown={isMobileView ? (e) => handlePressStart(trade.trade_id, e) : undefined}
                          onMouseUp={isMobileView ? handlePressEnd : undefined}
                          onMouseLeave={isMobileView ? handlePressEnd : undefined}
                          onTouchStart={isMobileView ? (e) => handlePressStart(trade.trade_id, e) : undefined}
                          onTouchEnd={isMobileView ? handlePressEnd : undefined}
                          style={{
                                  padding: isMobileView ? '0 0 0 2px' : '0 0 0 2px',
                                  cursor:  isMobileView ? 'pointer'   : undefined ,
                                  borderBottom: '0px'
                          }}
                        >
                          <td style={{textAlign: 'center', padding: '4px', verticalAlign: isMobileView ? 'middle' : 'middle' }} rowSpan={isMobileView ? 3 : 2}>
                            <input
                              type="checkbox"
                              checked={selectedTradeIds.includes(trade.trade_id)}
                              onChange={(e) => handleToggleSelectTrade(trade.trade_id, e)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </td>
                          <td style={{ color: rowColor, verticalAlign: 'middle' }}  className="hide-mobile"  rowSpan={isMobileView ? 2 : 2} >{trade.trade_id} </td>
                          <td
                            style={{ textAlign: 'left', color: rowColor, cursor: trade.notes ? 'help' : 'default', verticalAlign: isMobileView ? 'bottom' : 'bottom', lineHeight: isMobileView ? '1.0' : undefined }}
                            rowSpan={isMobileView ? 1 : 1}
                            title={trade.notes || ''}

                          >
                            <Link
                              to={`/trades/${trade.trade_id}`}
                              onClick={e => e.stopPropagation()}
                              style={{ color: 'inherit', textDecoration: 'none' }}
                            >
                              {trade.name || '---'}
                            </Link>
                          </td>
                          <td style={{ textAlign: 'left', color: rowColor, verticalAlign: isMobileView ? 'bottom' : 'bottom'}} className="hide-mobile">{trade.symbol_name || trade.symbol}</td>
                          <td style={{ textAlign: 'left', color: rowColor, verticalAlign: isMobileView ? 'bottom' : 'bottom', lineHeight: isMobileView ? '1.0' : undefined }}>
                            {trade.exchange1_name}
                          </td>
                          <td style={{ textAlign: 'left', color: rowColor, verticalAlign: isMobileView ? 'bottom' : 'bottom', lineHeight: isMobileView ? '1.0' : undefined }}>
                            {trade.exchange2_name}
                          </td>
                          <td style={{ textAlign: 'left', padding: isMobileView ? '0px 2px' : undefined, color: trade.realized_profit_usdt !== null && trade.realized_profit_usdt >= 0 ? '#3fb950' : trade.realized_profit_usdt !== null && trade.realized_profit_usdt < 0 ? '#f85149' : '#8b949e', verticalAlign: isMobileView ? 'bottom' : 'bottom', lineHeight: isMobileView ? '1.0' : undefined }} >
                            {trade.realized_profit_usdt !== null ? `${trade.realized_profit_usdt.toFixed(2)} USDT` : '--- USDT'}
                          </td>
                          <td className="hide-mobile" style={{ textAlign: 'left', color: rowColor, verticalAlign: isMobileView ? 'bottom' : 'bottom', lineHeight: isMobileView ? '1.0' : undefined }} >{formatTradeStatusCompact(trade.status)}</td>
                          <td className="hide-mobile" style={{ textAlign: 'left', color: rowColor, verticalAlign: 'bottom' }}  >{trade.jobs_scheduled || 0}/{trade.jobs_done || 0}/{trade.jobs_failed || 0}
                          </td>
                          <td className="hide-mobile" style={{ textAlign: 'left', padding: '2px 4px', lineHeight: '1.2', verticalAlign: 'bottom' }} rowSpan={isMobileView ? 1 : 2} >
                            <div style={{ color: '#8b949e', fontSize: '11px' }}>
                              {formatCreatedTimestamp(trade.created_timestamp).date}
                            </div>
                            <div style={{ color: '#8b949e', fontSize: '11px' }}>
                              {formatCreatedTimestamp(trade.created_timestamp).time}
                            </div>
                          </td>
                          <td className="hide-mobile" style={{textAlign: 'center', padding: '4px'}} rowSpan={isMobileView ? 1 : 2} >
                            <button
                              onClick={(e) => handleDeleteTrade(trade.trade_id, e)}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                padding: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'color 0.2s'
                              }}
                              onMouseEnter={(e) => e.target.style.color = '#f85149'}
                              onMouseLeave={(e) => e.target.style.color = '#8b949e'}
                              title="Delete trade"
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="currentColor"
                                style={{color: '#8b949e'}}
                              >
                                <path d="M11 1.75V3h2.25a.75.75 0 0 1 0 1.5H2.75a.75.75 0 0 1 0-1.5H5V1.75C5 .784 5.784 0 6.75 0h2.5C10.216 0 11 .784 11 1.75zM4.496 6.675l.66 6.6a.25.25 0 0 0 .249.225h5.19a.25.25 0 0 0 .249-.225l.66-6.6a.75.75 0 0 1 1.492.149l-.66 6.6A1.748 1.748 0 0 1 10.595 15h-5.19a1.75 1.75 0 0 1-1.741-1.575l-.66-6.6a.75.75 0 1 1 1.492-.15zM6.5 1.75V3h3V1.75a.25.25 0 0 0-.25-.25h-2.5a.25.25 0 0 0-.25.25z"></path>
                              </svg>
                            </button>
                          </td>
                        </tr>

                        {/* Desktop second line - only visible on desktop */}
                        {!isMobileView && (
                          <tr
                            onClick={() => navigate(`/trades/${trade.trade_id}`)}
                            className="desktop-second-line"
                          >
                            <td></td>
                            <td></td>
                            <td style={{ color: rowColor, verticalAlign: 'top' }}>
                              {trade.exchange1_contract}
                            </td>
                            <td style={{ color: rowColor, verticalAlign: 'top' }}>
                              {trade.exchange2_contract}
                            </td>
                            <td style={{ color: trade.realized_profit_percent !== null && trade.realized_profit_percent >= 0 ? '#3fb950' : trade.realized_profit_percent !== null && trade.realized_profit_percent < 0 ? '#f85149' : '#8b949e', verticalAlign: 'top' }}>
                                                          {trade.realized_profit_percent !== null ? `${trade.realized_profit_percent.toFixed(2)}%` : '--- %'}
                            </td>
                            <td></td>
                            <td></td>
                          </tr>
                        )}

                        {/* Mobile second line - only visible on mobile */}
                        {isMobileView && (
                          <tr
                            className={`mobile-second-line ${pressingTradeId === trade.trade_id ? 'pressing' : ''}`}
                            onClick={() => toggleMobileRow(trade.trade_id)}
                            onMouseDown={(e) => handlePressStart(trade.trade_id, e)}
                            onMouseUp={handlePressEnd}
                            onMouseLeave={handlePressEnd}
                            onTouchStart={(e) => handlePressStart(trade.trade_id, e)}
                            onTouchEnd={handlePressEnd}
                            style={{ cursor: 'pointer' }}
                          >
                            <td style={{ color: rowColor, verticalAlign: 'top' }}>
                              {trade.symbol_name || trade.symbol}
                            </td>
                            <td style={{ color: rowColor, verticalAlign: 'top' }}>
                              {trade.exchange1_contract}
                            </td>
                            <td style={{ color: rowColor, verticalAlign: 'top' }}>
                              {trade.exchange2_contract}
                            </td>
                            <td style={{ color: trade.realized_profit_percent !== null && trade.realized_profit_percent >= 0 ? '#3fb950' : trade.realized_profit_percent !== null && trade.realized_profit_percent < 0 ? '#f85149' : '#8b949e', verticalAlign: 'top' }}>
                              {trade.realized_profit_percent !== null ? `${trade.realized_profit_percent.toFixed(2)}%` : '---%'}
                            </td>
                          </tr>
                        )}

                        {/* Mobile third line - only visible on mobile */}
                        {isMobileView && (
                          <tr
                            className={`mobile-third-line ${pressingTradeId === trade.trade_id ? 'pressing' : ''}`}
                            onClick={() => toggleMobileRow(trade.trade_id)}
                            onMouseDown={(e) => handlePressStart(trade.trade_id, e)}
                            onMouseUp={handlePressEnd}
                            onMouseLeave={handlePressEnd}
                            onTouchStart={(e) => handlePressStart(trade.trade_id, e)}
                            onTouchEnd={handlePressEnd}
                            style={{ cursor: 'pointer' }}
                          >
                            <td style={{ color: rowColor, verticalAlign: 'top' }}>
                              {formatTradeStatusCompact(trade.status)}
                            </td>
                            <td style={{ color: '#8b949e', verticalAlign: 'top' }}>
                              {formatCreatedTimestamp(trade.created_timestamp).date}
                            </td>
                            <td style={{ color: '#8b949e', verticalAlign: 'top' }}>
                              {formatCreatedTimestamp(trade.created_timestamp).time}
                            </td>
                            <td style={{ color: rowColor, textAlign: 'left', verticalAlign: 'top' }}>
                              {trade.jobs_scheduled || 0}/{trade.jobs_done || 0}/{trade.jobs_failed || 0}
                            </td>
                          </tr>
                        )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
          </table>
        </div>
        )}
      </div>

      <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ color: '#8b949e', fontSize: '11px' }}>
          Showing {trades.length} of {totalRecords} trades
          {pageSize !== 'unlimited' && ` (Page ${currentPage} of ${Math.ceil(totalRecords / pageSize) || 1})`}
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <label style={{ color: '#8b949e', fontSize: '10px' }}>Trades per page:</label>
          <select
            value={pageSize}
            onChange={(e) => {
              const newSize = e.target.value === 'unlimited' ? 'unlimited' : parseInt(e.target.value);
              setPageSize(newSize);
              setCurrentPage(1);
            }}
            style={{
              padding: '4px 8px',
              fontSize: '10px',
              backgroundColor: '#21262d',
              border: '1px solid #2a2d3a',
              borderRadius: '3px',
              color: '#ffffff',
              width: '100px'
            }}
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={200}>200</option>
            <option value="unlimited">Unlimited</option>
          </select>
        </div>
      </div>

      {pageSize !== 'unlimited' && totalRecords > 0 && (
        <div style={{ marginTop: '15px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px' }}>
          <button
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
            style={{
              padding: '4px 12px',
              fontSize: '10px',
              backgroundColor: currentPage === 1 ? '#2a2d3a' : '#3fb950',
              border: 'none',
              borderRadius: '3px',
              color: currentPage === 1 ? '#6e7681' : '#ffffff',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
            }}
          >
            First
          </button>
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            style={{
              padding: '4px 12px',
              fontSize: '10px',
              backgroundColor: currentPage === 1 ? '#2a2d3a' : '#3fb950',
              border: 'none',
              borderRadius: '3px',
              color: currentPage === 1 ? '#6e7681' : '#ffffff',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
            }}
          >
            Previous
          </button>
          <span style={{ color: '#8b949e', fontSize: '10px' }}>
            Page {currentPage} of {Math.ceil(totalRecords / pageSize) || 1}
          </span>
          <button
            onClick={() => setCurrentPage(prev => prev + 1)}
            disabled={currentPage >= Math.ceil(totalRecords / pageSize)}
            style={{
              padding: '4px 12px',
              fontSize: '10px',
              backgroundColor: currentPage >= Math.ceil(totalRecords / pageSize) ? '#2a2d3a' : '#3fb950',
              border: 'none',
              borderRadius: '3px',
              color: currentPage >= Math.ceil(totalRecords / pageSize) ? '#6e7681' : '#ffffff',
              cursor: currentPage >= Math.ceil(totalRecords / pageSize) ? 'not-allowed' : 'pointer'
            }}
          >
            Next
          </button>
          <button
            onClick={() => setCurrentPage(Math.ceil(totalRecords / pageSize))}
            disabled={currentPage >= Math.ceil(totalRecords / pageSize)}
            style={{
              padding: '4px 12px',
              fontSize: '10px',
              backgroundColor: currentPage >= Math.ceil(totalRecords / pageSize) ? '#2a2d3a' : '#3fb950',
              border: 'none',
              borderRadius: '3px',
              color: currentPage >= Math.ceil(totalRecords / pageSize) ? '#6e7681' : '#ffffff',
              cursor: currentPage >= Math.ceil(totalRecords / pageSize) ? 'not-allowed' : 'pointer'
            }}
          >
            Last
          </button>
        </div>
      )}

      <NewTradeDialog
        isOpen={isDialogOpen}
        onClose={() => {
          setIsDialogOpen(false);
          setPrefillData(null);
        }}
        onTradeCreated={handleTradeCreated}
        prefillData={prefillData}
      />

      {/* Bulk Trade Job Dialog */}
      {showBulkTradeJobDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            background: '#0d1117',
            border: '1px solid #30363d',
            borderRadius: '6px',
            padding: '20px',
            minWidth: '400px',
            maxWidth: '500px',
            fontFamily: "'Courier New', Courier, monospace",
            color: '#c9d1d9'
          }}>
            <h3 style={{
              margin: '0 0 16px 0',
              fontSize: '14px',
              fontWeight: 'normal',
              color: '#ffffff',
              borderBottom: '1px solid #30363d',
              paddingBottom: '8px'
            }}>
              Create Trade Job for {selectedTradeIds.length} Trade(s)
            </h3>

            <div style={{ marginBottom: '12px' }}>
              <label style={{
                display: 'block',
                color: '#8b949e',
                fontSize: '12px',
                marginBottom: '4px'
              }}>
                Action Type:
              </label>
              <select
                value={bulkTradeJobDialogData.actionType}
                onChange={(e) => handleBulkTradeJobDialogChange('actionType', e.target.value)}
                style={{
                  width: '100%',
                  background: '#0d1117',
                  border: '1px solid #30363d',
                  borderRadius: '4px',
                  color: '#c9d1d9',
                  padding: '6px 8px',
                  fontFamily: "'Courier New', Courier, monospace",
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
              >
                <option value="OPEN-TRADE">OPEN-TRADE</option>
                <option value="CLOSE-TRADE">CLOSE-TRADE</option>
                <option value="FORCED-CLOSE-TRADE">FORCED-CLOSE-TRADE</option>
                <option value="CLOSE-AUTOMATIC">CLOSE-AUTOMATIC</option>
              </select>
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label style={{
                display: 'block',
                color: '#8b949e',
                fontSize: '12px',
                marginBottom: '4px'
              }}>
                Trigger Type:
              </label>
              <select
                value={bulkTradeJobDialogData.triggerType}
                onChange={(e) => handleBulkTradeJobDialogChange('triggerType', e.target.value)}
                disabled={bulkTradeJobDialogData.actionType === 'FORCED-CLOSE-TRADE'}
                style={{
                  width: '100%',
                  background: bulkTradeJobDialogData.actionType === 'FORCED-CLOSE-TRADE' ? '#161b22' : '#0d1117',
                  border: '1px solid #30363d',
                  borderRadius: '4px',
                  color: bulkTradeJobDialogData.actionType === 'FORCED-CLOSE-TRADE' ? '#6e7681' : '#c9d1d9',
                  padding: '6px 8px',
                  fontFamily: "'Courier New', Courier, monospace",
                  fontSize: '12px',
                  cursor: bulkTradeJobDialogData.actionType === 'FORCED-CLOSE-TRADE' ? 'not-allowed' : 'pointer'
                }}
              >
                <option value="TRADE_PROFIT">TRADE_PROFIT</option>
                <option value="TIME">TIME</option>
              </select>
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label style={{
                display: 'block',
                color: '#8b949e',
                fontSize: '12px',
                marginBottom: '4px'
              }}>
                Condition:
              </label>
              <select
                value={bulkTradeJobDialogData.condition}
                onChange={(e) => handleBulkTradeJobDialogChange('condition', e.target.value)}
                disabled={bulkTradeJobDialogData.actionType === 'FORCED-CLOSE-TRADE'}
                style={{
                  width: '100%',
                  background: bulkTradeJobDialogData.actionType === 'FORCED-CLOSE-TRADE' ? '#161b22' : '#0d1117',
                  border: '1px solid #30363d',
                  borderRadius: '4px',
                  color: bulkTradeJobDialogData.actionType === 'FORCED-CLOSE-TRADE' ? '#6e7681' : '#c9d1d9',
                  padding: '6px 8px',
                  fontFamily: "'Courier New', Courier, monospace",
                  fontSize: '12px',
                  cursor: bulkTradeJobDialogData.actionType === 'FORCED-CLOSE-TRADE' ? 'not-allowed' : 'pointer'
                }}
              >
                <option value=">">{'>'}</option>
                <option value=">=">{'>='}</option>
                <option value="<">{'<'}</option>
                <option value="<=">{'<='}</option>
              </select>
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label style={{
                display: 'block',
                color: '#8b949e',
                fontSize: '12px',
                marginBottom: '4px'
              }}>
                Trade Profit [%]:
              </label>
              <input
                type="text"
                value={bulkTradeJobDialogData.tradeProfit}
                onChange={(e) => handleBulkTradeJobDialogChange('tradeProfit', e.target.value)}
                disabled={bulkTradeJobDialogData.actionType === 'FORCED-CLOSE-TRADE'}
                placeholder="0.0000"
                style={{
                  width: '100%',
                  background: bulkTradeJobDialogData.actionType === 'FORCED-CLOSE-TRADE' ? '#161b22' : '#0d1117',
                  border: '1px solid #30363d',
                  borderRadius: '4px',
                  color: bulkTradeJobDialogData.actionType === 'FORCED-CLOSE-TRADE' ? '#6e7681' : '#c9d1d9',
                  padding: '6px 8px',
                  fontFamily: "'Courier New', Courier, monospace",
                  fontSize: '12px',
                  cursor: bulkTradeJobDialogData.actionType === 'FORCED-CLOSE-TRADE' ? 'not-allowed' : 'text'
                }}
              />
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label style={{
                display: 'block',
                color: '#8b949e',
                fontSize: '12px',
                marginBottom: '4px'
              }}>
                Volume:
              </label>
              <input
                type="text"
                value={bulkTradeJobDialogData.volume}
                onChange={(e) => handleBulkTradeJobDialogChange('volume', e.target.value)}
                placeholder="Enter volume"
                style={{
                  width: '100%',
                  background: '#0d1117',
                  border: '1px solid #30363d',
                  borderRadius: '4px',
                  color: '#c9d1d9',
                  padding: '6px 8px',
                  fontFamily: "'Courier New', Courier, monospace",
                  fontSize: '12px'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                color: '#8b949e',
                fontSize: '12px',
                marginBottom: '4px'
              }}>
                Scheduled Time:
              </label>
              <input
                type="datetime-local"
                value={bulkTradeJobDialogData.scheduledTime}
                onChange={(e) => handleBulkTradeJobDialogChange('scheduledTime', e.target.value)}
                style={{
                  width: '100%',
                  background: '#0d1117',
                  border: '1px solid #30363d',
                  borderRadius: '4px',
                  color: '#c9d1d9',
                  padding: '6px 8px',
                  fontFamily: "'Courier New', Courier, monospace",
                  fontSize: '12px'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowBulkTradeJobDialog(false);
                  setBulkAction('');
                }}
                style={{
                  padding: '6px 12px',
                  background: '#21262d',
                  border: '1px solid #30363d',
                  borderRadius: '4px',
                  color: '#c9d1d9',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontFamily: "'Courier New', Courier, monospace"
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmBulkTradeJob}
                style={{
                  padding: '6px 12px',
                  background: '#238636',
                  border: '1px solid #2ea043',
                  borderRadius: '4px',
                  color: '#ffffff',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontFamily: "'Courier New', Courier, monospace"
                }}
              >
                Create Jobs
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Trades;
